import { computed, ref } from 'vue';

// Step-by-step guides: the robot takes the user page by page, points at the button to
// press, and lists exactly what to fill in. Targets are CSS selectors; quick-action
// buttons expose data-action (DataTableQuickAction), tabs end their id with
// -trigger-<value>. A step whose target is missing still shows its text.

type Text = { en: string; km: string };
type Steps = { en: string[]; km: string[] };

export interface WalkStep {
  route: string; // route name
  query?: Record<string, string>; // opened with this query (e.g. a settings tab)
  alsoOn?: string[]; // other routes that count as this step's page (e.g. a wizard it opens)
  page: Text; // page name shown on "Take me there"
  target?: string;
  waitFor?: string; // move on by itself once this element appears (e.g. the form's next step)
  guide?: string; // a detailed guide for this step, offered as "Show me step by step"
  check?: string; // required step: "Done, next" unlocks once this check passes (walkthroughChecks.ts)
  optional?: boolean; // with check: the card also offers "Skip this step"
  doneWhen?: string; // like check, but done once this element is on the page (no API call)
  countUp?: string; // done once this record count (walkthroughChecks STEP_COUNTS) rises above its value when the step opened
  feature?: string; // plan feature the step needs; default: the page's requiresSubscription. Skipped without it.
  text: Text; // the goal of the step
  how?: Steps; // what to do on the page, in order
}

export interface WalkFlow {
  id: string;
  title: Text;
  steps: WalkStep[];
  hidden?: boolean; // only reachable from another guide's "Show me step by step"
  group?: WalkGroup; // heading in the guide list
}

export type WalkGroup = 'start' | 'daily' | 'teaching' | 'money' | 'people' | 'more';

const form = (who: string, n: number) => `[data-guide="${who}-form-${n}"]`;
const tab = (value: string) => `[role="tab"][id$="-trigger-${value}"]`;
const action = (id: string) => `[data-action="${id}"]`;

const P = {
  academicYear: { en: 'Academic Year', km: 'ឆ្នាំសិក្សា' },
  templates: { en: 'Reporting Period Templates', km: 'គំរូវគ្គវាយតម្លៃ' },
  reportPeriod: { en: 'Report Period', km: 'វគ្គវាយតម្លៃ' },
  shift: { en: 'Shift Management', km: 'គ្រប់គ្រងវេន' },
  class: { en: 'Class', km: 'ថ្នាក់' },
  subject: { en: 'Subjects', km: 'មុខវិជ្ជា' },
  course: { en: 'Course', km: 'វគ្គសិក្សា' },
  room: { en: 'Room', km: 'បន្ទប់' },
  teacher: { en: 'Teachers', km: 'គ្រូបង្រៀន' },
  student: { en: 'Students', km: 'សិស្ស' },
  feeTypes: { en: 'Fee Types', km: 'ប្រភេទកម្រៃ' },
  gradeFee: { en: 'Grade Fee Pricing', km: 'កម្រៃតាមកម្រិតថ្នាក់' },
  placementSettings: { en: 'School Settings → Placement test', km: 'ការកំណត់សាលា → តេស្តចូលរៀន' },
  pending: { en: 'Pending Applications', km: 'ពាក្យស្នើសុំកំពុងរង់ចាំ' },
  recordPayment: { en: 'Record Payment', km: 'កត់ត្រាការទូទាត់' },
  calendar: { en: 'Academic Calendar', km: 'ប្រតិទិនសិក្សា' },
  adminDashboard: { en: 'School Admin Dashboard', km: 'ផ្ទាំងគ្រប់គ្រងរដ្ឋបាលសាលា' },
  inbox: { en: 'Notification Inbox', km: 'ប្រអប់ការជូនដំណឹង' },
  subjectExam: { en: 'Subject Exam', km: 'ប្រឡងតាមមុខវិជ្ជា' },
  scores: { en: 'Score Management', km: 'ការគ្រប់គ្រងពិន្ទុ' },
  scoreReport: { en: 'Score Report', km: 'របាយការណ៍ពិន្ទុ' },
  aiScheduling: { en: 'AI Scheduling', km: 'កាលវិភាគ AI' },
  attendanceSettings: { en: 'School Settings → Attendance', km: 'ការកំណត់សាលា → វត្តមាន' },
  policy: { en: 'Attendance Policy', km: 'គោលនយោបាយវត្តមាន' },
  attendance: { en: 'Student attendance', km: 'វត្តមានសិស្ស' },
  promotion: { en: 'Promotion Criteria', km: 'លក្ខណៈវិនិច្ឆ័យឡើងថ្នាក់' },
  transition: { en: 'Academic Year Transition', km: 'ការផ្លាស់ប្តូរឆ្នាំសិក្សា' },
  enrollExisting: { en: 'Enroll Existing Student', km: 'ចុះឈ្មោះសិស្សដែលមានស្រាប់' },
  teacherSessions: { en: 'Teacher Sessions', km: 'វគ្គបង្រៀនគ្រូ' },
  availabilitySettings: { en: 'Availability settings', km: 'ការកំណត់ពេលទំនេរគ្រូ' },
  availability: { en: 'Teacher Availability', km: 'ម៉ោងទំនេរគ្រូបង្រៀន' },
  availabilityApproval: { en: 'Approval Requests', km: 'សំណើសុំអនុម័ត' },
  transfers: { en: 'Student transfers', km: 'ការផ្ទេរសិស្ស' },
  idCards: { en: 'Print student ID cards', km: 'បោះពុម្ពបណ្ណសម្គាល់សិស្ស' },
  parents: { en: 'Parents', km: 'អាណាព្យាបាល' },
  staff: { en: 'Staff', km: 'បុគ្គលិក' },
  usersSettings: { en: 'School Settings → Users & Permissions', km: 'ការកំណត់សាលា → អ្នកប្រើ និងសិទ្ធិ' },
  letterGrade: { en: 'Letter Grade', km: 'ចំណាត់ថ្នាក់និទ្ទេស' },
  calcPolicy: { en: 'Report Calculation Policies', km: 'គោលការណ៍គណនារបាយការណ៍' },
  classCourseReport: { en: 'Class Course Report', km: 'របាយការណ៍ពិន្ទុតាមថ្នាក់ និងមុខវិជ្ជា' },
  leaderboard: { en: 'Leaderboard', km: 'តារាងចំណាត់ថ្នាក់' },
  homework: { en: 'Homework', km: 'កិច្ចការផ្ទះ' },
  quiz: { en: 'Quiz', km: 'ការប្រលងសាកល្បង' },
  anonymousExam: { en: 'Anonymous Exam', km: 'ការប្រឡងអនាមិក' },
  drivers: { en: 'Drivers', km: 'អ្នកបើកបរ' },
  transport: { en: 'Transport Management', km: 'ការគ្រប់គ្រងដឹកជញ្ជូន' },
  passes: { en: 'Student passes', km: 'ប័ណ្ណសិស្ស' },
  trips: { en: 'Daily Trips', km: 'ដំណើរប្រចាំថ្ងៃ' },
  liveTracking: { en: 'Live tracking', km: 'តាមដានផ្ទាល់' },
  transportReports: { en: 'Transport reports', km: 'របាយការណ៍ដឹកជញ្ជូន' },
  feeSchedules: { en: 'Fee Schedule Management', km: 'គ្រប់គ្រងកាលវិភាគថ្លៃ' },
  paymentMethods: { en: 'Enrollment Payment Methods', km: 'វិធីបង់ប្រាក់សម្រាប់ការចុះឈ្មោះ' },
  enrollmentSettings: { en: 'Enrollment Settings', km: 'ការកំណត់ការចុះឈ្មោះ' },
  invoices: { en: 'Invoices & Receipts', km: 'វិក្កយបត្រ និង បង្កាន់ដៃ' },
  refunds: { en: 'Manage Refunds', km: 'គ្រប់គ្រងការសងប្រាក់វិញ' },
  settlement: { en: 'MES Clearing & School Settlement', km: 'ការទូទាត់ MES និងការទូទាត់ទៅសាលា' },
  push: { en: 'Push Notifications', km: 'Push Notifications' },
  chat: { en: 'Chat & Messaging', km: 'ការជជែកកម្សាន្ត និងផ្ញើសារ' },
  moderation: { en: 'Chat Moderation', km: 'ការគ្រប់គ្រងការជជែក' },
  notificationCategories: { en: 'Notification Categories', km: 'ប្រភេទការជូនដំណឹង' },
  notificationStats: { en: 'Notification Statistics', km: 'ស្ថិតិការជូនដំណឹង' },
  branches: { en: 'Branch Management', km: 'ការគ្រប់គ្រងសាខា' },
  templatesDocs: { en: 'Document Templates', km: 'គំរូឯកសារ' },
  scheduleJobs: { en: 'Schedule Jobs', km: 'កុងការកំណត់ពេលវេលា' },
  faculties: { en: 'Faculties', km: 'មហាវិទ្យាល័យ' },
  departments: { en: 'Departments', km: 'នាយកដ្ឋាន' },
  programs: { en: 'Programs', km: 'កម្មវិធីសិក្សា' },
  shortCourses: { en: 'Short Courses', km: 'វគ្គសិក្សាខ្លី' },
  batches: { en: 'Course Batches', km: 'ជំនាន់វគ្គសិក្សា' },
  certificates: { en: 'Certificates', km: 'វិញ្ញាបនបត្រ' },
  dashboard: { en: 'Dashboard', km: 'ផ្ទាំងគ្រប់គ្រង' },
  aiDashboard: { en: 'AI Dashboard', km: 'ផ្ទាំងគ្រប់គ្រង AI' },
  documentRequests: { en: 'Document Requests', km: 'សំណើឯកសារ' },
  library: { en: 'Library', km: 'បណ្ណាល័យ' },
  assets: { en: 'Equipment and Assets', km: 'សម្ភារៈ និងទ្រព្យសម្បត្តិ' },
  pickup: { en: 'Student Pickup', km: 'ការទទួលសិស្ស' },
  teacherStorage: { en: 'Teacher Storage', km: 'ទំហំផ្ទុកគ្រូ' },
  attendanceFollowUp: { en: 'Attendance Follow-up', km: 'ការតាមដានវត្តមាន' },
  feeFollowUp: { en: 'Fee Collection Follow-up', km: 'ការតាមដានការបង់ថ្លៃ' },
};

// The Create Student form, step by step. Part of "Admit a new student", and the
// "Show me step by step" of the new-school guide.
const STUDENT_FORM_STEPS: WalkStep[] = [
  {
    route: 'StudentPage', page: P.student, target: action('create-student'), waitFor: form('student', 1),
    text: { en: 'The student and their parent each get a login for MES Mobile: timetable, attendance, scores, fees and leave.', km: 'សិស្ស និងអាណាព្យាបាល ម្នាក់ៗទទួលបានគណនីសម្រាប់ MES Mobile៖ កាលវិភាគ វត្តមាន ពិន្ទុ កម្រៃ និងច្បាប់។' },
    how: {
      en: ['Press Create Student. The form has 4 steps; I explain each one.', 'Many students at once? Use Import student by excel instead (download the sample file first).'],
      km: ['ចុច បង្កើតសិស្ស។ ទម្រង់មាន ៤ ជំហាន ខ្ញុំពន្យល់ម្តងមួយៗ។', 'សិស្សច្រើនក្នុងពេលតែមួយ? ប្រើ នាំចូលសិស្សដោយ Excel ជំនួសវិញ (ទាញយកឯកសារគំរូជាមុន)។'],
    },
  },
  {
    route: 'StudentPage', page: P.student, waitFor: form('student', 2),
    text: { en: 'Step 1 · Student and parent.', km: 'ជំហាន ១ · សិស្ស និងអាណាព្យាបាល។' },
    how: {
      en: ['Name in Khmer, and in Latin letters for documents.', 'Phone number, gender and date of birth (required).', 'Parent: choose an existing parent or add one, then the relationship (father, mother, guardian). Mark the main contact.', 'Address and ID document (national card, passport, family book or birth certificate): optional.', 'Press Next.'],
      km: ['ឈ្មោះជាខ្មែរ និងជាអក្សរឡាតាំងសម្រាប់ឯកសារ។', 'លេខទូរស័ព្ទ ភេទ និងថ្ងៃខែឆ្នាំកំណើត (ចាំបាច់)។', 'អាណាព្យាបាល៖ ជ្រើសអាណាព្យាបាលដែលមានស្រាប់ ឬបន្ថែមថ្មី រួចទំនាក់ទំនង (ឪពុក ម្តាយ អាណាព្យាបាល)។ កំណត់អ្នកទំនាក់ទំនងចម្បង។', 'អាសយដ្ឋាន និងឯកសារអត្តសញ្ញាណ (អត្តសញ្ញាណប័ណ្ណ លិខិតឆ្លងដែន សៀវភៅគ្រួសារ ឬសំបុត្រកំណើត)៖ ស្រេចចិត្ត។', 'ចុច បន្ទាប់។'],
    },
  },
  {
    route: 'StudentPage', page: P.student, waitFor: form('student', 3),
    text: { en: 'Step 2 · Where the student studies.', km: 'ជំហាន ២ · សិស្សរៀននៅណា។' },
    how: {
      en: ['Academic year and branch.', 'Grade: fees for this grade are taken from Grade Fee Pricing.', 'Class: the class they join. If the grade needs a placement test, leave it empty; the student waits in Pending Applications.', 'Press Next.'],
      km: ['ឆ្នាំសិក្សា និងសាខា។', 'កម្រិតថ្នាក់៖ កម្រៃនៃកម្រិតនេះយកពី កម្រៃតាមកម្រិតថ្នាក់។', 'ថ្នាក់៖ ថ្នាក់ដែលគេចូលរៀន។ បើកម្រិតនោះត្រូវការតេស្ត ទុកទទេ សិស្សនឹងរង់ចាំនៅ ពាក្យស្នើសុំកំពុងរង់ចាំ។', 'ចុច បន្ទាប់។'],
    },
  },
  {
    route: 'StudentPage', page: P.student, waitFor: form('student', 4),
    text: { en: 'Step 3 · Login for MES Mobile.', km: 'ជំហាន ៣ · គណនីសម្រាប់ MES Mobile។' },
    how: {
      en: ['Username: tick Auto use phone number, or type one.', 'Password: tick Auto generate password, or type one and confirm it.', 'Press Next.'],
      km: ['ឈ្មោះអ្នកប្រើ៖ ធីក ប្រើលេខទូរស័ព្ទស្វ័យប្រវត្តិ ឬវាយបញ្ចូល។', 'ពាក្យសម្ងាត់៖ ធីក បង្កើតពាក្យសម្ងាត់ស្វ័យប្រវត្តិ ឬវាយបញ្ចូល ហើយបញ្ជាក់។', 'ចុច បន្ទាប់។'],
    },
  },
  {
    route: 'StudentPage', page: P.student, countUp: 'students',
    text: { en: 'Step 4 · Review, fees and create.', km: 'ជំហាន ៤ · ពិនិត្យ កម្រៃ និងបង្កើត។' },
    how: {
      en: ['Check the details and the fees of the grade.', 'Press Create. To register siblings or a class, press Save & Add Another; all queued students are submitted together.', 'A box shows the login. Give it to the parent for MES Mobile.', 'The parent can pay by KHQR, or you record the payment in Record Payment.'],
      km: ['ពិនិត្យព័ត៌មាន និងកម្រៃនៃកម្រិតថ្នាក់។', 'ចុច បង្កើត។ ដើម្បីចុះឈ្មោះបងប្អូន ឬថ្នាក់ទាំងមូល ចុច រក្សាទុក និងបន្ថែមមួយទៀត សិស្សក្នុងជួរទាំងអស់ត្រូវដាក់ស្នើជាមួយគ្នា។', 'ប្រអប់មួយបង្ហាញគណនី។ ផ្តល់ឲ្យអាណាព្យាបាលប្រើ MES Mobile។', 'អាណាព្យាបាលអាចបង់តាម KHQR ឬអ្នកកត់ត្រាការបង់នៅ កត់ត្រាការទូទាត់។'],
    },
  },
];

export const WALK_FLOWS: WalkFlow[] = [
  {
    id: 'setup',
    group: 'start',
    title: { en: 'Set up a new school', km: 'រៀបចំសាលាថ្មី' },
    steps: [
      {
        route: 'AcademicYearPage', page: P.academicYear, target: action('create-academic-year'), check: 'academicYear',
        text: { en: 'Create the academic year. Everything else belongs to it.', km: 'បង្កើតឆ្នាំសិក្សា។ អ្វីៗផ្សេងទៀតស្ថិតក្នុងឆ្នាំនេះ។' },
        how: {
          en: ['Press the highlighted button.', 'Name it (e.g. 2026–2027) and pick the start and end dates.', 'Term: yearly, 2 semesters, 3 trimesters or 4 quarters.', 'Create. The year starts as a draft.'],
          km: ['ចុចប៊ូតុងដែលភ្លឺ។', 'ដាក់ឈ្មោះ (ឧ. ២០២៦–២០២៧) ហើយជ្រើសថ្ងៃចាប់ផ្តើម និងថ្ងៃបញ្ចប់។', 'ប្រភេទឆមាស៖ ប្រចាំឆ្នាំ ២ឆមាស ៣ត្រីមាស ឬ ៤ត្រីមាស។', 'ចុច បង្កើត។ ឆ្នាំសិក្សាចាប់ផ្តើមជាសេចក្តីព្រាង។'],
        },
      },
      {
        route: 'AcademicYearPage', alsoOn: ['AcademicYearSetupWizardPage'], page: P.academicYear, target: action('complete-year-setup'), check: 'yearReady',
        text: { en: 'Finish the setup wizard to activate the year. Skip if the year is already active.', km: 'បញ្ចប់ជំនួយការរៀបចំ ដើម្បីបើកដំណើរការឆ្នាំសិក្សា។ រំលងបើឆ្នាំសិក្សាដំណើរការរួចហើយ។' },
        how: {
          en: ['Press Complete Setup.', 'Setup method: Fresh create, or Clone from previous year to copy its classes and periods.', 'Classes: pick your school type, the grades you teach and how many classes per grade (7A, 7B…).', 'Reporting periods: choose Use template.', 'Activate. You are taken to the template page next.'],
          km: ['ចុច បញ្ចប់ការរៀបចំ។', 'វិធីរៀបចំ៖ បង្កើតថ្មី ឬ ចម្លងពីឆ្នាំមុន ដើម្បីចម្លងថ្នាក់ និងវគ្គ។', 'ថ្នាក់៖ ជ្រើសប្រភេទសាលា កម្រិតថ្នាក់ដែលបង្រៀន និងចំនួនថ្នាក់ក្នុងកម្រិតនីមួយៗ (៧ក ៧ខ…)។', 'វគ្គវាយតម្លៃ៖ ជ្រើស ប្រើគំរូ។', 'ចុច បើកដំណើរការ។ អ្នកនឹងទៅទំព័រគំរូបន្ទាប់។'],
        },
      },
      {
        route: 'ReportingPeriodTemplatesPage', page: P.templates, target: '[data-tour="template-generate"]', check: 'reportPeriods',
        text: { en: 'Generate the reporting periods: months, semesters and the year, linked together. Exams and report cards use them.', km: 'បង្កើតវគ្គវាយតម្លៃ៖ ខែ ឆមាស និងឆ្នាំ ដែលភ្ជាប់គ្នា។ ការប្រឡង និងសៀវភៅតាមដានប្រើវគ្គទាំងនេះ។' },
        how: {
          en: ['Check your template is marked Assigned. If not, select it on the left and press Assign template.', 'Press Generate periods.', 'Check the dates of each month and semester, then confirm.'],
          km: ['ពិនិត្យថាគំរូរបស់អ្នកមានស្លាក បានកំណត់។ បើមិនទាន់ ជ្រើសវានៅខាងឆ្វេង ហើយចុច កំណត់គំរូ។', 'ចុច បង្កើតវគ្គវាយតម្លៃ។', 'ពិនិត្យកាលបរិច្ឆេទនៃខែ និងឆមាសនីមួយៗ រួចបញ្ជាក់។'],
        },
      },
      {
        route: 'ReportPeriodPage', page: P.reportPeriod,
        text: { en: 'Check the reporting periods.', km: 'ពិនិត្យវគ្គវាយតម្លៃ។' },
        how: {
          en: ['Each month should sit under its semester, each semester under the year.', 'Fix a wrong date with Edit.', 'If a semester appears twice, delete the extra one.'],
          km: ['ខែនីមួយៗគួរស្ថិតក្រោមឆមាសរបស់វា ឆមាសនីមួយៗស្ថិតក្រោមឆ្នាំ។', 'កែកាលបរិច្ឆេទខុសដោយចុច កែប្រែ។', 'បើឆមាសលេចឡើងពីរដង សូមលុបមួយចេញ។'],
        },
      },
      {
        route: 'ShiftPage', page: P.shift, target: action('create-shift'), check: 'shift',
        text: { en: 'Add your shifts. Lessons, check-in and attendance follow them.', km: 'បន្ថែមវេន។ មេរៀន ការចុះវត្តមានគ្រូ និងវត្តមានសិស្ស អនុវត្តតាមវេន។' },
        how: {
          en: ['Press Create Shift.', 'Name (e.g. Morning Shift) and a short code (e.g. morning).', 'Start and end time, and the days of the week.', 'Keep it active. Repeat for the afternoon or evening shift.'],
          km: ['ចុច បង្កើតវេន។', 'ឈ្មោះ (ឧ. វេនព្រឹក) និងកូដខ្លី (ឧ. morning)។', 'ម៉ោងចាប់ផ្តើម ម៉ោងបញ្ចប់ និងថ្ងៃក្នុងសប្តាហ៍។', 'ទុកឲ្យសកម្ម។ ធ្វើម្តងទៀតសម្រាប់វេនរសៀល ឬវេនល្ងាច។'],
        },
      },
      {
        route: 'ClassPage', page: P.class, target: action('create-class'), check: 'class',
        text: { en: 'Check the classes the wizard created.', km: 'ពិនិត្យថ្នាក់ដែលជំនួយការបានបង្កើត។' },
        how: {
          en: ['You should see every class of every grade.', 'Missing one? Press Create Class: name (e.g. 7C), grade and academic year.', 'Need another grade later? Add it on the Grade page, then its classes here.', 'Class teachers are set on the Teachers page later.'],
          km: ['អ្នកគួរឃើញថ្នាក់ទាំងអស់នៃកម្រិតនីមួយៗ។', 'ខ្វះថ្នាក់? ចុច បង្កើតថ្នាក់៖ ឈ្មោះ (ឧ. ៧គ) កម្រិតថ្នាក់ និងឆ្នាំសិក្សា។', 'ត្រូវការកម្រិតថ្នាក់ផ្សេងទៀតពេលក្រោយ? បន្ថែមវានៅទំព័រ កម្រិតថ្នាក់ រួចបង្កើតថ្នាក់របស់វានៅទីនេះ។', 'គ្រូប្រចាំថ្នាក់ត្រូវកំណត់នៅទំព័រគ្រូបង្រៀននៅពេលក្រោយ។'],
        },
      },
      {
        route: 'SubjectPage', page: P.subject, target: action('create-subject'), check: 'subject',
        text: { en: 'Add the subjects your school teaches.', km: 'បន្ថែមមុខវិជ្ជាដែលសាលាបង្រៀន។' },
        how: {
          en: ['Press Create Subject.', 'Name in English and Khmer, and a code (e.g. MATH).', 'Pick a category (or create one) and a color for the timetable.', 'Display order sets the order in lists. Repeat for each subject.'],
          km: ['ចុច បង្កើតមុខវិជ្ជា។', 'ឈ្មោះជាអង់គ្លេស និងខ្មែរ និងកូដ (ឧ. MATH)។', 'ជ្រើសប្រភេទ (ឬបង្កើតថ្មី) និងពណ៌សម្រាប់កាលវិភាគ។', 'លំដាប់បង្ហាញកំណត់លំដាប់ក្នុងបញ្ជី។ ធ្វើម្តងទៀតសម្រាប់មុខវិជ្ជានីមួយៗ។'],
        },
      },
      {
        route: 'CoursePage', page: P.course, target: action('create-course'), check: 'course',
        text: { en: 'Create the courses: one subject taught in one grade, linked to the classes that study it.', km: 'បង្កើតវគ្គសិក្សា៖ មុខវិជ្ជាមួយបង្រៀនក្នុងកម្រិតថ្នាក់មួយ ភ្ជាប់ទៅថ្នាក់ដែលរៀនវា។' },
        how: {
          en: ['Press Create Course with Subject.', 'Pick the subject, grade and academic year. The name and code are made for you.', 'Credit hours = the normal hours per week (e.g. 4 = four 60-minute lessons). The timetable uses it unless you change it for one year or one class (see the last point).', 'Tick Assign to class immediately and pick the first class.', 'Other classes of the grade (7B, 7C…): press Assign Course To Class. A class only gets lessons, exams and a timetable for linked courses.', 'Lesson length: press Edit on the course to set the main session length (e.g. 60 min).', 'Only one year or one class is different? Course menu → View Detail: the Academic years tab sets weekly hours for that year; the Classes tab → Schedule Config sets them for one class. Order used: class → year → credit hours.'],
          km: ['ចុច បង្កើតវគ្គសិក្សាជាមួយមុខវិជ្ជា។', 'ជ្រើសមុខវិជ្ជា កម្រិតថ្នាក់ និងឆ្នាំសិក្សា។ ឈ្មោះ និងកូដនឹងបង្កើតដោយស្វ័យប្រវត្តិ។', 'ម៉ោងក្រេឌីត = ម៉ោងធម្មតាក្នុងមួយសប្តាហ៍ (ឧ. ៤ = មេរៀន ៦០ នាទី ចំនួន ៤)។ កាលវិភាគប្រើវា លុះត្រាតែអ្នកប្តូរសម្រាប់ឆ្នាំមួយ ឬថ្នាក់មួយ (មើលចំណុចចុងក្រោយ)។', 'ធីក ភ្ជាប់ទៅថ្នាក់ភ្លាមៗ ហើយជ្រើសថ្នាក់ទីមួយ។', 'ថ្នាក់ផ្សេងទៀតនៃកម្រិតនោះ (៧ខ ៧គ…)៖ ចុច ភ្ជាប់វគ្គសិក្សាទៅថ្នាក់។ ថ្នាក់មួយមានមេរៀន ការប្រឡង និងកាលវិភាគ តែសម្រាប់វគ្គសិក្សាដែលបានភ្ជាប់។', 'រយៈពេលមេរៀន៖ ចុច កែប្រែ លើវគ្គសិក្សា ដើម្បីកំណត់រយៈពេលមេរៀនចម្បង (ឧ. ៦០ នាទី)។', 'មានតែឆ្នាំមួយ ឬថ្នាក់មួយខុសគេ? ម៉ឺនុយវគ្គសិក្សា → មើលលម្អិត៖ ផ្ទាំង ឆ្នាំសិក្សា កំណត់ម៉ោងប្រចាំសប្តាហ៍សម្រាប់ឆ្នាំនោះ ផ្ទាំង ថ្នាក់រៀន → កំណត់កាលវិភាគ កំណត់សម្រាប់ថ្នាក់មួយ។ លំដាប់ប្រើ៖ ថ្នាក់ → ឆ្នាំ → ម៉ោងក្រេឌីត។'],
        },
      },
      {
        route: 'RoomPage', page: P.room, target: action('create-room'), check: 'room',
        text: { en: 'Add your rooms. AI Scheduling places lessons in them.', km: 'បន្ថែមបន្ទប់។ កាលវិភាគ AI ដាក់មេរៀនក្នុងបន្ទប់ទាំងនេះ។' },
        how: {
          en: ['Press Create Room.', 'Name (e.g. Room 101) and capacity (how many students fit).', 'Repeat for each room.', 'Optional: on the Class page, edit a class and pick its Home room. The AI timetable keeps that class there.'],
          km: ['ចុច បង្កើតបន្ទប់។', 'ឈ្មោះ (ឧ. បន្ទប់ ១០១) និងចំណុះ (ចំនួនសិស្សដែលអាចផ្ទុកបាន)។', 'ធ្វើម្តងទៀតសម្រាប់បន្ទប់នីមួយៗ។', 'ស្រេចចិត្ត៖ នៅទំព័រ ថ្នាក់ កែប្រែថ្នាក់ ហើយជ្រើស បន្ទប់ប្រចាំថ្នាក់។ កាលវិភាគ AI រក្សាថ្នាក់នោះនៅទីនោះ។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher, target: action('create-teacher'), guide: 'teacher', check: 'teacher',
        text: { en: 'Register your teachers and link them to their classes and courses.', km: 'ចុះឈ្មោះគ្រូ ហើយភ្ជាប់ពួកគេទៅថ្នាក់ និងវគ្គសិក្សា។' },
        how: {
          en: ['Press Show me step by step: the robot explains every field of the teacher form.', 'Come back here when your teachers are added.'],
          km: ['ចុច បង្ហាញខ្ញុំម្តងមួយជំហាន៖ រ៉ូបូតពន្យល់គ្រប់ចន្លោះនៃទម្រង់គ្រូ។', 'ត្រឡប់មកទីនេះវិញ ពេលបន្ថែមគ្រូរួច។'],
        },
      },
      {
        route: 'StudentPage', page: P.student, target: action('create-student'), guide: 'student', check: 'student',
        text: { en: 'Last: register your students. Your school is ready!', km: 'ចុងក្រោយ៖ ចុះឈ្មោះសិស្ស។ សាលារបស់អ្នករួចរាល់ហើយ!' },
        how: {
          en: ['Press Show me step by step: the robot explains every field of the student form.', 'Many students? Use Import student by excel instead.'],
          km: ['ចុច បង្ហាញខ្ញុំម្តងមួយជំហាន៖ រ៉ូបូតពន្យល់គ្រប់ចន្លោះនៃទម្រង់សិស្ស។', 'សិស្សច្រើន? ប្រើ នាំចូលសិស្សដោយ Excel ជំនួសវិញ។'],
        },
      },
    ],
  },
  {
    id: 'admission',
    group: 'start',
    title: { en: 'Admit a new student', km: 'ទទួលសិស្សថ្មី' },
    steps: [
      {
        route: 'FeeTypesPage', page: P.feeTypes, target: action('add-fee-type'), check: 'feeType',
        text: { en: 'Once a year: create your fee types. Skip if they exist.', km: 'ម្តងក្នុងមួយឆ្នាំ៖ បង្កើតប្រភេទកម្រៃ។ រំលងបើមានរួចហើយ។' },
        how: {
          en: ['Press Add Fee Type.', 'Name and code (e.g. Tuition / TUITION), category and default amount.', 'Recurring: charged every month or term. Required: every student pays it.', 'Save. Repeat for books, uniform, transport…'],
          km: ['ចុច បន្ថែមប្រភេទកម្រៃ។', 'ឈ្មោះ និងកូដ (ឧ. ថ្លៃសិក្សា / TUITION) ប្រភេទ និងចំនួនលំនាំដើម។', 'កើតឡើងដដែលៗ៖ គិតរៀងរាល់ខែ ឬឆមាស។ ចាំបាច់៖ សិស្សគ្រប់គ្នាបង់។', 'រក្សាទុក។ ធ្វើម្តងទៀតសម្រាប់សៀវភៅ ឯកសណ្ឋាន ដឹកជញ្ជូន…'],
        },
      },
      {
        route: 'GradeFeePricingPage', page: P.gradeFee, target: action('add-pricing'), check: 'gradePricing',
        text: { en: 'Set the price of each fee for every grade.', km: 'កំណត់តម្លៃកម្រៃនីមួយៗសម្រាប់កម្រិតថ្នាក់នីមួយៗ។' },
        how: {
          en: ['Press Add Pricing.', 'Pick the grade, fee type and academic year.', 'Enter the amount and billing frequency. Leave branch empty to apply to all branches.', 'Save. Repeat for each grade.'],
          km: ['ចុច បន្ថែមតម្លៃ។', 'ជ្រើសកម្រិតថ្នាក់ ប្រភេទកម្រៃ និងឆ្នាំសិក្សា។', 'បញ្ចូលចំនួន និងប្រេកង់គិតប្រាក់។ ទុកសាខាទទេ ដើម្បីអនុវត្តគ្រប់សាខា។', 'រក្សាទុក។ ធ្វើម្តងទៀតសម្រាប់កម្រិតថ្នាក់នីមួយៗ។'],
        },
      },
      {
        route: 'SettingPage', query: { tab: 'placement' }, page: P.placementSettings, feature: 'student_enrollment',
        text: { en: 'Decide if new students take a placement test. It is off by default; skip if you don\'t use it.', km: 'សម្រេចថាសិស្សថ្មីត្រូវប្រឡងចូលរៀនឬអត់។ វាបិទតាមលំនាំដើម រំលងបើមិនប្រើ។' },
        how: {
          en: ['Turn the feature on.', 'Choose the grades that require a test.', 'Choose how retakes are scored and what happens on a fail.', 'Save.'],
          km: ['បើកមុខងារនេះ។', 'ជ្រើសកម្រិតថ្នាក់ដែលត្រូវការតេស្ត។', 'ជ្រើសរបៀបដាក់ពិន្ទុពេលប្រឡងឡើងវិញ និងអ្វីកើតឡើងពេលធ្លាក់។', 'រក្សាទុក។'],
        },
      },
      ...STUDENT_FORM_STEPS,
      {
        route: 'EnrollmentApplicationsPage', page: P.pending, feature: 'student_enrollment',
        text: { en: 'Enroll students who took a placement test or still have no class.', km: 'ចុះឈ្មោះសិស្សដែលបានប្រឡងចូលរៀន ឬមិនទាន់មានថ្នាក់ ចូលថ្នាក់។' },
        how: {
          en: ['Pending tests: record the placement test score (or Remove test).', 'Placement results: press Enroll on the student.', 'Choose the class and the payment option: full, partial, registration only or installments.', 'Create enrollment. The student now waits for payment (free enrollments are done).'],
          km: ['តេស្តកំពុងរង់ចាំ៖ កត់ត្រាពិន្ទុតេស្តចូលរៀន (ឬ លុបតេស្ត)។', 'លទ្ធផលតេស្ត៖ ចុច ចុះឈ្មោះ លើសិស្ស។', 'ជ្រើសថ្នាក់ និងជម្រើសបង់ប្រាក់៖ បង់ពេញ បង់ខ្លះ ចុះឈ្មោះតែប៉ុណ្ណោះ ឬបង់រំលស់។', 'ចុច បង្កើតការចុះឈ្មោះ។ សិស្សរង់ចាំការបង់ប្រាក់ (ការចុះឈ្មោះឥតគិតថ្លៃរួចរាល់)។'],
        },
      },
      {
        route: 'RecordPaymentPage', page: P.recordPayment,
        text: { en: 'Collect the fee.', km: 'ទទួលប្រាក់កម្រៃ។' },
        how: {
          en: ['Search the student by name or ID and pick the enrollment.', 'Check the amount due.', 'Choose the method (cash, bank transfer, KHQR or ABA PayWay) and the amount, then record it.', 'A receipt is issued and the parent sees it in MES Mobile. With KHQR the parent scans and it confirms by itself.'],
          km: ['ស្វែងរកសិស្សតាមឈ្មោះ ឬលេខសម្គាល់ ហើយជ្រើសការចុះឈ្មោះ។', 'ពិនិត្យចំនួនដែលត្រូវបង់។', 'ជ្រើសវិធីបង់ (សាច់ប្រាក់ ផ្ទេរតាមធនាគារ KHQR ឬ ABA PayWay) និងចំនួន រួចកត់ត្រា។', 'បង្កាន់ដៃនឹងចេញ ហើយអាណាព្យាបាលឃើញក្នុង MES Mobile។ ជាមួយ KHQR អាណាព្យាបាលស្កេន ហើយវាបញ្ជាក់ដោយខ្លួនឯង។'],
        },
      },
    ],
  },
  {
    id: 'exams',
    group: 'teaching',
    title: { en: 'Exams → report cards', km: 'ប្រឡង → សៀវភៅតាមដាន' },
    steps: [
      {
        route: 'ReportPeriodPage', page: P.reportPeriod,
        text: { en: 'Every exam belongs to a reporting period. Check this period exists.', km: 'ការប្រឡងនីមួយៗស្ថិតក្នុងវគ្គវាយតម្លៃ។ ពិនិត្យថាវគ្គនេះមានហើយ។' },
        how: {
          en: ['Find the month or semester of the exam (e.g. October 2026).', 'Missing? Generate it from Reporting Period Templates.'],
          km: ['រកខែ ឬឆមាសនៃការប្រឡង (ឧ. តុលា ២០២៦)។', 'មិនមាន? បង្កើតវាពី គំរូវគ្គវាយតម្លៃ។'],
        },
      },
      {
        route: 'AcademicCalendarPage', page: P.calendar, target: '[data-tour="calendar-actions"]', check: 'generalExam',
        text: { en: 'Create the general exam: the exam period of the whole school (e.g. October Monthly Exam). Subject exams belong to it.', km: 'បង្កើតការប្រឡងទូទៅ៖ រយៈពេលប្រឡងរបស់សាលាទាំងមូល (ឧ. ការប្រឡងប្រចាំខែតុលា)។ ការប្រឡងតាមមុខវិជ្ជាស្ថិតក្នុងវា។' },
        how: {
          en: ['Press Action, then Create general exam.', 'Exam name (e.g. October Monthly Exam) and the start and end dates of the exam days.', 'Report Period: the month or semester its scores count for.', 'Grades: which grades sit it. Empty = the whole school.', 'Allow Teacher Exams: teachers can create the subject exams themselves in MES Mobile.', 'Score Entry Mode: when teachers may enter scores. Auto = after the exam ends; Anytime; Is Completed = after you mark it completed; Manual = when you open scoring.'],
          km: ['ចុច សកម្មភាព រួច បង្កើតការប្រឡង។', 'ឈ្មោះការប្រឡង (ឧ. ការប្រឡងប្រចាំខែតុលា) និងថ្ងៃចាប់ផ្តើម និងបញ្ចប់នៃថ្ងៃប្រឡង។', 'វគ្គវាយតម្លៃ៖ ខែ ឬឆមាសដែលពិន្ទុរបស់វារាប់បញ្ចូល។', 'កម្រិតថ្នាក់៖ កម្រិតណាខ្លះប្រឡង។ ទុកទទេ = សាលាទាំងមូល។', 'អនុញ្ញាតឱ្យគ្រូបង្កើតប្រឡង៖ គ្រូអាចបង្កើតការប្រឡងតាមមុខវិជ្ជាដោយខ្លួនឯងក្នុង MES Mobile។', 'របៀបបញ្ចូលពិន្ទុ៖ ពេលណាគ្រូអាចបញ្ចូលពិន្ទុ។ ស្វ័យប្រវត្តិ = ក្រោយការប្រឡងបញ្ចប់ គ្រប់ពេល បានបញ្ចប់ = ក្រោយអ្នកសម្គាល់ថាបញ្ចប់ ដោយដៃ = ពេលអ្នកបើកការដាក់ពិន្ទុ។'],
        },
      },
      {
        route: 'SubjectExamPage', page: P.subjectExam, target: action('create-exam'),
        text: { en: 'Create the subject exams. Teachers can also create them in MES Mobile.', km: 'បង្កើតការប្រឡងតាមមុខវិជ្ជា។ គ្រូក៏អាចបង្កើតក្នុង MES Mobile ដែរ។' },
        how: {
          en: ['Press Create Exam.', 'General Exam: pick the one you created on the Academic Calendar.', 'Name, date, start time, duration and max score.', 'Score policy: how it counts in the report (normal, excluded, scaled or bonus).', 'Grade, class, course and room. Repeat for each subject.'],
          km: ['ចុច បង្កើតការប្រឡង។', 'ការប្រឡងទូទៅ៖ ជ្រើសការប្រឡងដែលអ្នកបានបង្កើតនៅប្រតិទិនសិក្សា។', 'ឈ្មោះ កាលបរិច្ឆេទ ម៉ោងចាប់ផ្តើម រយៈពេល និងពិន្ទុអតិបរមា។', 'គោលការណ៍ពិន្ទុ៖ របៀបគិតក្នុងរបាយការណ៍ (ធម្មតា មិនរាប់ ធ្វើមាត្រដ្ឋាន ឬបន្ថែម)។', 'កម្រិតថ្នាក់ ថ្នាក់ វគ្គសិក្សា និងបន្ទប់។ ធ្វើម្តងទៀតសម្រាប់មុខវិជ្ជានីមួយៗ។'],
        },
      },
      {
        route: 'SubjectExamPage', page: P.subjectExam, target: action('group-exam'),
        text: { en: 'Optional: combine exams into one score (e.g. Khmer Written = dictation + essay).', km: 'ស្រេចចិត្ត៖ បញ្ចូលការប្រឡងជាពិន្ទុមួយ (ឧ. ខ្មែរសរសេរ = សរសេរតាមអាន + តែងសេចក្តី)។' },
        how: {
          en: ['Choose the general exam and the class first; Group exams appears.', 'Name and max score of the group, then select two or more exams. Their max scores must add up to the group\'s max.', 'Create group, then set its letter-grade scale on the Letter Grade page.'],
          km: ['ជ្រើសការប្រឡងទូទៅ និងថ្នាក់ជាមុនសិន ប៊ូតុង ដាក់ការប្រឡងជាក្រុម នឹងលេចឡើង។', 'ឈ្មោះ និងពិន្ទុអតិបរមារបស់ក្រុម រួចជ្រើសការប្រឡងពីរ ឬច្រើន។ ពិន្ទុអតិបរមារបស់វាត្រូវបូកស្មើនឹងពិន្ទុអតិបរមារបស់ក្រុម។', 'បង្កើតក្រុម រួចកំណត់មាត្រដ្ឋាននិទ្ទេសរបស់វានៅទំព័រ ចំណាត់ថ្នាក់និទ្ទេស។'],
        },
      },
      {
        route: 'ScoreManagementPage', page: P.scores, target: tab('approve_score'),
        text: { en: 'Approve the scores teachers submit.', km: 'អនុម័តពិន្ទុដែលគ្រូដាក់ស្នើ។' },
        how: {
          en: ['Teachers enter scores in MES Mobile and press Submit for Approval.', 'Approve Score: choose the exam and check the list.', 'Select records, then Approve Selected Records, or Reject with a note.', 'Rejected goes back to the teacher. Request Update Score lists teachers asking to change approved scores.', 'You can also enter scores yourself with Assign Score.'],
          km: ['គ្រូបញ្ចូលពិន្ទុក្នុង MES Mobile ហើយចុច ដាក់ស្នើអនុម័ត។', 'អនុម័តពិន្ទុ៖ ជ្រើសការប្រឡង ហើយពិនិត្យបញ្ជី។', 'ជ្រើសកំណត់ត្រា រួចចុច អនុម័តកំណត់ត្រាដែលបានជ្រើស ឬ បដិសេធ ជាមួយកំណត់សម្គាល់។', 'ការបដិសេធត្រឡប់ទៅគ្រូវិញ។ សំណើកែពិន្ទុ បង្ហាញគ្រូដែលសុំកែពិន្ទុដែលបានអនុម័ត។', 'អ្នកក៏អាចបញ្ចូលពិន្ទុដោយខ្លួនឯងតាម ដាក់ពិន្ទុ។'],
        },
      },
      {
        route: 'ScoreReportPage', page: P.scoreReport,
        text: { en: 'See the results and report cards.', km: 'មើលលទ្ធផល និងសៀវភៅតាមដាន។' },
        how: {
          en: ['Pick the class and the period: month, semester or year.', 'For yearly results, generate the term reports.', 'Averages, ranks and letter grades follow Report Calculation Policies and Letter Grade.', 'Parents see results in MES Mobile once approved.'],
          km: ['ជ្រើសថ្នាក់ និងវគ្គ៖ ខែ ឆមាស ឬឆ្នាំ។', 'សម្រាប់លទ្ធផលប្រចាំឆ្នាំ បង្កើតរបាយការណ៍វគ្គ។', 'មធ្យមភាគ ចំណាត់ថ្នាក់ និងនិទ្ទេស អនុវត្តតាម គោលការណ៍គណនារបាយការណ៍ និង ចំណាត់ថ្នាក់និទ្ទេស។', 'អាណាព្យាបាលឃើញលទ្ធផលក្នុង MES Mobile ក្រោយអនុម័ត។'],
        },
      },
    ],
  },
  {
    id: 'timetable',
    group: 'teaching',
    title: { en: 'Build the timetable', km: 'រៀបចំកាលវិភាគ' },
    steps: [
      {
        route: 'ShiftPage', page: P.shift, target: action('create-shift'), check: 'shift',
        text: { en: 'Check your shifts: lessons are placed inside a shift\'s times and days.', km: 'ពិនិត្យវេន៖ មេរៀនត្រូវដាក់ក្នុងម៉ោង និងថ្ងៃនៃវេន។' },
        how: {
          en: ['Each shift needs the right start and end time and school days.', 'A class that already has lessons keeps its shift; a class can have both, some subjects in the morning and others in the afternoon. You pick shifts for the other classes in the planner.'],
          km: ['វេននីមួយៗត្រូវមានម៉ោងចាប់ផ្តើម ម៉ោងបញ្ចប់ និងថ្ងៃសិក្សាត្រឹមត្រូវ។', 'ថ្នាក់ដែលមានមេរៀនរួចហើយ រក្សាវេនរបស់វា។ ថ្នាក់មួយអាចមានទាំងពីរ៖ មុខវិជ្ជាខ្លះពេលព្រឹក ខ្លះពេលរសៀល។ អ្នកជ្រើសវេនសម្រាប់ថ្នាក់ផ្សេងទៀតនៅក្នុងកម្មវិធីរៀបចំកាលវិភាគ។'],
        },
      },
      {
        route: 'RoomPage', page: P.room, target: action('create-room'), check: 'room',
        text: { en: 'Make sure every room is listed with its capacity.', km: 'ត្រូវប្រាកដថាបន្ទប់ទាំងអស់មានក្នុងបញ្ជី ព្រមទាំងចំណុះ។' },
        how: {
          en: ['A lesson only goes in a room big enough for all its students. Combined classes need one room for everyone.', 'Without a home room, the AI uses the smallest free room that fits, so a class may change rooms between lessons.'],
          km: ['មេរៀនដាក់តែក្នុងបន្ទប់ដែលធំល្មមសម្រាប់សិស្សទាំងអស់។ ថ្នាក់ដែលរៀនរួមគ្នាត្រូវការបន្ទប់មួយសម្រាប់គ្រប់គ្នា។', 'បើគ្មានបន្ទប់ប្រចាំថ្នាក់ AI ប្រើបន្ទប់ទំនេរតូចបំផុតដែលល្មម ដូច្នេះថ្នាក់អាចប្តូរបន្ទប់រវាងមេរៀន។'],
        },
      },
      {
        route: 'ClassPage', page: P.class,
        text: { en: 'Optional: give each class a home room, so it stays in one room.', km: 'ស្រេចចិត្ត៖ កំណត់បន្ទប់ប្រចាំថ្នាក់ ដើម្បីឲ្យថ្នាក់នៅបន្ទប់តែមួយ។' },
        how: {
          en: ['Edit a class and pick its Home room.', 'The AI puts the class there first. It uses another room only when the home room is busy or too small, or a room rule sends a subject elsewhere (e.g. Chemistry in the lab).', 'Morning and afternoon classes can share a home room.'],
          km: ['កែប្រែថ្នាក់ ហើយជ្រើស បន្ទប់ប្រចាំថ្នាក់។', 'AI ដាក់ថ្នាក់នៅទីនោះមុនគេ។ វាប្រើបន្ទប់ផ្សេងតែពេលបន្ទប់ប្រចាំថ្នាក់ជាប់រវល់ ឬតូចពេក ឬច្បាប់បន្ទប់ដាក់មុខវិជ្ជាទៅកន្លែងផ្សេង (ឧ. គីមីវិទ្យានៅមន្ទីរពិសោធន៍)។', 'ថ្នាក់ពេលព្រឹក និងថ្នាក់ពេលរសៀលអាចប្រើបន្ទប់ប្រចាំថ្នាក់រួមគ្នា។'],
        },
      },
      {
        route: 'CoursePage', page: P.course, target: action('assign-to-class'),
        text: { en: 'Each class needs its courses linked, with weekly hours and lesson length. Only linked courses are scheduled.', km: 'ថ្នាក់នីមួយៗត្រូវភ្ជាប់វគ្គសិក្សា ជាមួយម៉ោងប្រចាំសប្តាហ៍ និងរយៈពេលមេរៀន។ មានតែវគ្គសិក្សាដែលបានភ្ជាប់ទេ ទើបដាក់ក្នុងកាលវិភាគ។' },
        how: {
          en: ['Assign Course To Class for any course a class is missing.', 'Weekly hours: credit hours by default. For one year: View Detail → Academic years. For one class: View Detail → Classes → Schedule Config. Order used: class → year → credit hours.', 'Edit a course to set the main session length (e.g. 60 min) and the max session length for double lessons.'],
          km: ['ភ្ជាប់វគ្គសិក្សាទៅថ្នាក់ សម្រាប់វគ្គដែលថ្នាក់នៅខ្វះ។', 'ម៉ោងប្រចាំសប្តាហ៍៖ ម៉ោងក្រេឌីតតាមលំនាំដើម។ សម្រាប់ឆ្នាំមួយ៖ មើលលម្អិត → ឆ្នាំសិក្សា។ សម្រាប់ថ្នាក់មួយ៖ មើលលម្អិត → ថ្នាក់រៀន → កំណត់កាលវិភាគ។ លំដាប់ប្រើ៖ ថ្នាក់ → ឆ្នាំ → ម៉ោងក្រេឌីត។', 'កែប្រែវគ្គសិក្សា ដើម្បីកំណត់រយៈពេលមេរៀនចម្បង (ឧ. ៦០ នាទី) និងរយៈពេលអតិបរមាសម្រាប់មេរៀនទ្វេ។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher,
        text: { en: 'Each class\'s course needs a teacher.', km: 'វគ្គសិក្សានីមួយៗនៃថ្នាក់ ត្រូវការគ្រូ។' },
        how: {
          en: ['A teacher\'s Teaching step (grade, class, course) assigns them to that class and course.', 'Missing a teacher? You can pick one in the planner\'s Check data step, or let the system pick a qualified teacher who submitted availability.'],
          km: ['ជំហានការបង្រៀនរបស់គ្រូ (កម្រិតថ្នាក់ ថ្នាក់ វគ្គសិក្សា) ចាត់តាំងគេទៅថ្នាក់ និងវគ្គនោះ។', 'ខ្វះគ្រូ? អ្នកអាចជ្រើសគ្រូនៅជំហាន ពិនិត្យទិន្នន័យ នៃកម្មវិធីរៀបចំកាលវិភាគ ឬឲ្យប្រព័ន្ធជ្រើសគ្រូមានសមត្ថភាពដែលបានដាក់ពេលទំនេរ។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'School timetable: open a round so teachers send when they are free.', km: 'កាលវិភាគសាលា៖ បើកវគ្គ ដើម្បីឲ្យគ្រូផ្ញើពេលទំនេររបស់គេ។' },
        how: {
          en: ['Press New collection round.', 'Name (e.g. Semester 2 availability). Timetable period: the dates the new timetable will apply.', 'Submission window: when teachers can answer. Pick the target teachers.', 'Create draft round, then Open submissions.'],
          km: ['ចុច វគ្គប្រមូលថ្មី។', 'ឈ្មោះ (ឧ. ពេលទំនេរឆមាសទី២)។ រយៈពេលកាលវិភាគ៖ ថ្ងៃដែលកាលវិភាគថ្មីនឹងអនុវត្ត។', 'រយៈពេលដាក់ស្នើ៖ ពេលដែលគ្រូអាចឆ្លើយ។ ជ្រើស គ្រូក្នុងវគ្គ។', 'ចុច បង្កើតសេចក្ដីព្រាងវគ្គ រួច បើកការដាក់ស្នើ។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Accept every teacher\'s availability, then finalize the round.', km: 'ទទួលយកពេលទំនេររបស់គ្រូគ្រប់គ្នា រួចបញ្ចប់វគ្គ។' },
        how: {
          en: ['Teachers send their free time from MES Mobile.', 'On the round card, press Review submissions. Open each one: Accept submission, or Request correction with a note. An empty list means the teacher is not available.', 'Close submissions when the window ends, then press Review & finalize.', 'Finalize availability snapshot. It is blocked until every teacher is accepted. The live timetable does not change yet.'],
          km: ['គ្រូផ្ញើពេលទំនេររបស់គេពី MES Mobile។', 'នៅលើកាតវគ្គ ចុច ពិនិត្យការដាក់ស្នើ។ បើកការដាក់ស្នើនីមួយៗ៖ ទទួលយកការដាក់ស្នើ ឬ ស្នើសុំកែតម្រូវ ជាមួយកំណត់សម្គាល់។ បញ្ជីទទេ មានន័យថាគ្រូមិនទំនេរ។', 'បិទការដាក់ស្នើ ពេលរយៈពេលបញ្ចប់ រួចចុច ពិនិត្យ និងបញ្ចប់។', 'ចុច បញ្ចប់ការប្រមូលពេលទំនេរ។ វាត្រូវបានបិទ រហូតដល់គ្រូគ្រប់គ្នាត្រូវបានទទួលយក។ កាលវិភាគបច្ចុប្បន្នមិនទាន់ផ្លាស់ប្តូរទេ។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage', 'AISchedulingTimetablePage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Open the timetable planner and fix what the data check finds.', km: 'បើកកម្មវិធីរៀបចំកាលវិភាគ ហើយកែអ្វីដែលការពិនិត្យទិន្នន័យរកឃើញ។' },
        how: {
          en: ['In the finalized round, press Open timetable planner. It has 5 steps.', 'Step 1 · Check data: errors stop the timetable from filling (e.g. a teacher with too little availability, no room big enough). Warnings run on a default.', 'Pick missing teachers right in the list, or open the page the problem links to. Press Check again after a fix.'],
          km: ['នៅក្នុងវគ្គដែលបានបញ្ចប់ ចុច បើកកម្មវិធីរៀបចំកាលវិភាគ។ វាមាន ៥ ជំហាន។', 'ជំហាន ១ · ពិនិត្យទិន្នន័យ៖ កំហុសរារាំងកាលវិភាគមិនឲ្យពេញ (ឧ. គ្រូមានពេលទំនេរតិចពេក គ្មានបន្ទប់ធំល្មម)។ ការព្រមាន ប្រើតម្លៃលំនាំដើម។', 'ជ្រើសគ្រូដែលខ្វះនៅក្នុងបញ្ជីផ្ទាល់ ឬបើកទំព័រដែលបញ្ហាភ្ជាប់ទៅ។ ចុច ពិនិត្យម្តងទៀត ក្រោយពេលកែ។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage', 'AISchedulingTimetablePage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Step 2 · Classes & teachers: choose what to schedule.', km: 'ជំហាន ២ · ថ្នាក់ និងគ្រូ៖ ជ្រើសអ្វីដែលត្រូវរៀបចំ។' },
        how: {
          en: ['Classes: tick the classes to schedule. A class that already has lessons keeps its own shift. For the others, click a shift to apply it to every class.', 'Breaks (optional), e.g. 10:00–10:15 every day.', 'Subjects and teachers: change a teacher for this draft only. Tick "If a teacher is missing or too busy…" to let the system pick another qualified teacher.', 'Teach classes together: Combine whole classes merges every subject the chosen classes share (one teacher, one room). Or combine one subject. Remove all splits a set again.', 'Keep some current lessons where they are, if you like them.'],
          km: ['ថ្នាក់៖ ធីកថ្នាក់ដែលត្រូវរៀបចំ។ ថ្នាក់ដែលមានមេរៀនរួចហើយ រក្សាវេនផ្ទាល់ខ្លួន។ សម្រាប់ថ្នាក់ផ្សេងទៀត ចុចលើវេនដើម្បីអនុវត្តលើគ្រប់ថ្នាក់។', 'ពេលសម្រាក (ស្រេចចិត្ត) ឧ. ១០:០០–១០:១៥ រាល់ថ្ងៃ។', 'មុខវិជ្ជា និងគ្រូ៖ ប្តូរគ្រូសម្រាប់សេចក្តីព្រាងនេះតែប៉ុណ្ណោះ។ ធីក «បើគ្មានគ្រូ ឬគ្រូរវល់ពេក…» ដើម្បីឲ្យប្រព័ន្ធជ្រើសគ្រូមានសមត្ថភាពផ្សេង។', 'បង្រៀនថ្នាក់រួមគ្នា៖ បញ្ចូលថ្នាក់ទាំងមូល បញ្ចូលគ្រប់មុខវិជ្ជាដែលថ្នាក់ដែលបានជ្រើសរៀនដូចគ្នា (គ្រូម្នាក់ បន្ទប់មួយ)។ ឬបញ្ចូលមុខវិជ្ជាមួយ។ ដកចេញទាំងអស់ បំបែកវាវិញ។', 'រក្សាមេរៀនបច្ចុប្បន្នខ្លះនៅកន្លែងដើម បើអ្នកពេញចិត្ត។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage', 'AISchedulingTimetablePage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Step 3 · Rules (optional): tell the timetable what the school needs.', km: 'ជំហាន ៣ · ច្បាប់ (ស្រេចចិត្ត)៖ ប្រាប់កាលវិភាគពីតម្រូវការរបស់សាលា។' },
        how: {
          en: ['Describe rules in your own words (e.g. Math only in the morning; Mr. Dara is off on Friday), then Suggest rules and keep the ones you want.', 'Or Add a rule: time window, at most N a day, avoid the last period, only in these rooms, protect from holidays, teacher day off.', 'Each rule is Must, Important or Nice to have.'],
          km: ['ពិពណ៌នាច្បាប់ជាពាក្យរបស់អ្នក (ឧ. គណិតវិទ្យាតែពេលព្រឹក លោក ដារ៉ា ឈប់ថ្ងៃសុក្រ) រួចចុច ស្នើច្បាប់ ហើយរក្សាច្បាប់ដែលអ្នកចង់បាន។', 'ឬ បន្ថែមច្បាប់៖ ចន្លោះម៉ោង អតិបរមា N ដងក្នុងមួយថ្ងៃ ជៀសម៉ោងចុងក្រោយ តែក្នុងបន្ទប់ទាំងនេះ ការពារពីថ្ងៃឈប់សម្រាក ថ្ងៃឈប់របស់គ្រូ។', 'ច្បាប់នីមួយៗជា ត្រូវតែ សំខាន់ ឬ ល្អបើមាន។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage', 'AISchedulingTimetablePage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Step 4 · Generate & review: create a draft and adjust it.', km: 'ជំហាន ៤ · បង្កើត និងពិនិត្យ៖ បង្កើតសេចក្តីព្រាង ហើយកែតម្រូវ។' },
        how: {
          en: ['Press Generate draft. New or changed, moved, reassigned and removed lessons are marked.', 'Hours still missing? Press Explain & fix with AI, or Place a lesson yourself.', 'In the Week view, click a lesson to change its teacher, room, day or time. Or Ask AI to change this draft (e.g. "Move 7A Physics off Friday").', 'Press Validate and save edits.'],
          km: ['ចុច បង្កើតសេចក្តីព្រាង។ មេរៀនថ្មី ឬផ្លាស់ប្តូរ ផ្លាស់ទី ប្តូរគ្រូ និងលុប ត្រូវបានសម្គាល់។', 'នៅខ្វះម៉ោង? ចុច ពន្យល់ និងកែដោយ AI ឬ ដាក់មេរៀនមួយ ដោយខ្លួនឯង។', 'ក្នុង មើលតាមសប្តាហ៍ ចុចលើមេរៀន ដើម្បីប្តូរគ្រូ បន្ទប់ ថ្ងៃ ឬម៉ោង។ ឬ សុំ AI ឱ្យកែសេចក្តីព្រាង (ឧ. «ផ្លាស់រូបវិទ្យា 7A ចេញពីថ្ងៃសុក្រ»)។', 'ចុច ផ្ទៀងផ្ទាត់ និងរក្សាទុក។'],
        },
      },
      {
        route: 'AISchedulingPage', alsoOn: ['TeacherAvailabilityRoundsPage', 'AISchedulingTimetablePage'], page: P.aiScheduling, feature: 'teacher_availability',
        text: { en: 'Step 5 · Publish: it appears on every teacher\'s and student\'s phone.', km: 'ជំហាន ៥ · ផ្សព្វផ្សាយ៖ វាបង្ហាញលើទូរស័ព្ទគ្រូ និងសិស្សទាំងអស់។' },
        how: {
          en: ['Publishing is blocked while required hours are missing or edits are unsaved.', 'Publish reviewed timetable. It applies only to the round\'s timetable dates, must start after today, and keeps earlier attendance.'],
          km: ['ការផ្សព្វផ្សាយត្រូវបានបិទ ខណៈម៉ោងដែលត្រូវការនៅខ្វះ ឬការកែប្រែមិនទាន់រក្សាទុក។', 'ចុច ផ្សព្វផ្សាយកាលវិភាគដែលបានពិនិត្យ។ វាអនុវត្តតែក្នុងថ្ងៃនៃកាលវិភាគរបស់វគ្គ ត្រូវចាប់ផ្តើមក្រោយថ្ងៃនេះ ហើយរក្សាវត្តមានមុនៗ។'],
        },
      },
    ],
  },
  {
    id: 'attendance',
    group: 'daily',
    title: { en: 'Attendance & leave', km: 'វត្តមាន និងច្បាប់' },
    steps: [
      {
        route: 'SettingPage', query: { tab: 'attendance' }, page: P.attendanceSettings,
        text: { en: 'Choose how attendance and leave work at your school.', km: 'ជ្រើសរបៀបដែលវត្តមាន និងច្បាប់ដំណើរការនៅសាលារបស់អ្នក។' },
        how: {
          en: ['Attendance tracking mode: per class session or per round.', 'Class teacher can approve leave up to N days (default 1). Longer leave comes to the school.', 'Class monitor attendance: a student monitor may take attendance; the teacher confirms it.', 'Auto checkout time for teachers. Save.'],
          km: ['របៀបតាមដានវត្តមាន៖ តាមម៉ោងសិក្សា ឬតាមជុំ។', 'គ្រូប្រចាំថ្នាក់អាចអនុម័តច្បាប់រហូតដល់ N ថ្ងៃ (លំនាំដើម ១)។ ច្បាប់វែងជាងនេះមកសាលា។', 'វត្តមានដោយប្រធានថ្នាក់៖ សិស្សប្រធានថ្នាក់អាចកត់វត្តមាន ហើយគ្រូបញ្ជាក់។', 'ម៉ោងចេញស្វ័យប្រវត្តិសម្រាប់គ្រូ។ រក្សាទុក។'],
        },
      },
      {
        route: 'StudentPage', page: P.student,
        text: { en: 'If class monitor attendance is on, choose each class\'s monitor.', km: 'បើបើកវត្តមានដោយប្រធានថ្នាក់ ជ្រើសប្រធានថ្នាក់នៃថ្នាក់នីមួយៗ។' },
        how: {
          en: ['Find the student, open the row menu and choose Set as class monitor. A Monitor badge appears.', 'The class teacher can also set or change the monitor in MES Mobile.', 'The monitor takes attendance in MES Mobile; the teacher confirms it.'],
          km: ['រកសិស្ស បើកម៉ឺនុយជួរ ហើយជ្រើស កំណត់ជាប្រធានថ្នាក់។ ស្លាក ប្រធានថ្នាក់ នឹងលេចឡើង។', 'គ្រូប្រចាំថ្នាក់ក៏អាចកំណត់ ឬប្តូរប្រធានថ្នាក់ក្នុង MES Mobile ដែរ។', 'ប្រធានថ្នាក់កត់វត្តមានក្នុង MES Mobile គ្រូបញ្ជាក់។'],
        },
      },
      {
        route: 'AttendancePolicyPage', page: P.policy,
        text: { en: 'Set how attendance counts in reports.', km: 'កំណត់របៀបគិតវត្តមានក្នុងរបាយការណ៍។' },
        how: {
          en: ['Late / early departure: how many equal one absence.', 'Daily counting formula, and whether excused absence counts as present.', 'Score deductions, and automatic fail after unexcused absences.', 'Save. Reports update at the next calculation.'],
          km: ['មកយឺត / ចេញមុន៖ ប៉ុន្មានដងស្មើនឹងអវត្តមានមួយ។', 'រូបមន្តគិតប្រចាំថ្ងៃ និងថាតើអវត្តមានមានច្បាប់រាប់ជាវត្តមានឬអត់។', 'ការកាត់ពិន្ទុ និងធ្លាក់ស្វ័យប្រវត្តិក្រោយអវត្តមានគ្មានច្បាប់។', 'រក្សាទុក។ របាយការណ៍នឹងធ្វើបច្ចុប្បន្នភាពនៅការគណនាបន្ទាប់។'],
        },
      },
      {
        route: 'AttendanceStudentPage', page: P.attendance, target: tab('daily_attendance'),
        text: { en: 'Review attendance. Teachers mark it in MES Mobile.', km: 'ពិនិត្យវត្តមាន។ គ្រូកត់វត្តមានក្នុង MES Mobile។' },
        how: {
          en: ['Daily: one day by class.', 'Monthly and Monthly range: totals per student.', 'Request Update Attendance: teachers asking to change a past mark. Approve or reject.'],
          km: ['ប្រចាំថ្ងៃ៖ មួយថ្ងៃតាមថ្នាក់។', 'ប្រចាំខែ និងចន្លោះខែ៖ សរុបតាមសិស្ស។', 'សំណើកែវត្តមាន៖ គ្រូសុំកែវត្តមានចាស់។ អនុម័ត ឬបដិសេធ។'],
        },
      },
      {
        route: 'AttendanceStudentPage', page: P.attendance, target: tab('request_permission'),
        text: { en: 'Handle leave requests the class teacher can\'t approve.', km: 'ដោះស្រាយសំណើច្បាប់ដែលគ្រូប្រចាំថ្នាក់មិនអាចអនុម័ត។' },
        how: {
          en: ['Parents request leave in MES Mobile; the class teacher approves leave up to your limit.', 'Longer leave appears here: pick the grade.', 'Check the reason and attachment, then approve or reject.', 'Approved leave marks the student Excused for those days.'],
          km: ['អាណាព្យាបាលស្នើសុំច្បាប់ក្នុង MES Mobile គ្រូប្រចាំថ្នាក់អនុម័តច្បាប់រហូតដល់ចំនួនដែលអ្នកកំណត់។', 'ច្បាប់វែងជាងនេះបង្ហាញនៅទីនេះ៖ ជ្រើសកម្រិតថ្នាក់។', 'ពិនិត្យមូលហេតុ និងឯកសារភ្ជាប់ រួចអនុម័ត ឬបដិសេធ។', 'ច្បាប់ដែលបានអនុម័តកត់សិស្សថា មានច្បាប់ សម្រាប់ថ្ងៃទាំងនោះ។'],
        },
      },
    ],
  },
  {
    id: 'attendance-follow-up',
    group: 'daily',
    title: { en: 'Follow up repeated absences', km: 'តាមដានអវត្តមានញឹកញាប់' },
    steps: [
      {
        route: 'AttendanceFollowUpPage', page: P.attendanceFollowUp, target: action('rules'),
        text: { en: 'Decide when a student needs follow-up.', km: 'សម្រេចថាពេលណាសិស្សត្រូវការការតាមដាន។' },
        how: {
          en: ['Press Rules. A case opens when any limit is reached in the last N days (7–90): unexcused absences, absences in a row, or absence rate once enough lessons are marked.', 'Only marks recorded as absent count. Excused and unmarked lessons never do; late marks are not absences.', 'The Attendance Policy\'s daily counting mode decides what one absence is (lesson, shift or day).', 'Keep Automatic evaluation on and save.'],
          km: ['ចុច លក្ខខណ្ឌ។ ករណីបើកនៅពេលដល់កម្រិតណាមួយក្នុង N ថ្ងៃចុងក្រោយ (៧–៩០)៖ អវត្តមានគ្មានច្បាប់ អវត្តមានជាប់ៗគ្នា ឬអត្រាអវត្តមាន ពេលមានម៉ោងកត់ត្រាគ្រប់គ្រាន់។', 'មានតែម៉ោងដែលកត់ថាអវត្តមានប៉ុណ្ណោះដែលរាប់។ មានច្បាប់ និងមិនទាន់កត់ មិនរាប់ទេ ការមកយឺតមិនមែនជាអវត្តមាន។', 'របៀបរាប់ប្រចាំថ្ងៃក្នុងគោលនយោបាយវត្តមាន កំណត់ថាអវត្តមានមួយជាអ្វី (ម៉ោង វេន ឬថ្ងៃ)។', 'ទុកការវាយតម្លៃស្វ័យប្រវត្តិឲ្យបើក ហើយរក្សាទុក។'],
        },
      },
      {
        route: 'AttendanceFollowUpPage', page: P.attendanceFollowUp, target: action('reconcile'),
        text: { en: 'Find the students who need follow-up.', km: 'ស្វែងរកសិស្សដែលត្រូវការការតាមដាន។' },
        how: {
          en: ['Cases appear by themselves: every student is checked in the background.', 'Press Evaluate attendance to check everyone now.', 'Filter by owner, grade or class; Advanced filters add status, Mine / Unassigned and due date.'],
          km: ['ករណីលេចឡើងដោយខ្លួនឯង៖ សិស្សគ្រប់គ្នាត្រូវបានពិនិត្យនៅខាងក្រោយ។', 'ចុច វាយតម្លៃវត្តមាន ដើម្បីពិនិត្យគ្រប់គ្នាឥឡូវ។', 'ត្រងតាមអ្នកទទួលបន្ទុក កម្រិតថ្នាក់ ឬថ្នាក់ តម្រងកម្រិតខ្ពស់បន្ថែមស្ថានភាព ខ្ញុំទទួលបន្ទុក / គ្មានអ្នកទទួលបន្ទុក និងកាលកំណត់។'],
        },
      },
      {
        route: 'AttendanceFollowUpPage', page: P.attendanceFollowUp,
        text: { en: 'Work a case until it is resolved.', km: 'ដោះស្រាយករណីរហូតដល់ចប់។' },
        how: {
          en: ['View case: absences when the case opened, now, and since it opened.', 'Assign owner: a teacher or staff member who follows the student.', 'Log contact or a planned action; every step needs a note and is kept in the history.', 'Request parent explanation sends the parents a notification. It does not excuse the absences.', 'Change the status (In progress, Monitoring, Escalated). Completed closes the case.'],
          km: ['មើលករណី៖ អវត្តមាននៅពេលបើកករណី ឥឡូវ និងតាំងពីបើក។', 'កំណត់អ្នកទទួលបន្ទុក៖ គ្រូ ឬបុគ្គលិកដែលតាមដានសិស្ស។', 'កត់ទំនាក់ទំនង ឬសកម្មភាពគ្រោងទុក ជំហាននីមួយៗត្រូវការកំណត់ចំណាំ និងរក្សាក្នុងប្រវត្តិ។', 'ស្នើសុំការពន្យល់ពីអាណាព្យាបាល ផ្ញើការជូនដំណឹងទៅអាណាព្យាបាល។ វាមិនធ្វើឲ្យអវត្តមានក្លាយជាមានច្បាប់ទេ។', 'ប្តូរស្ថានភាព (កំពុងដំណើរការ កំពុងតាមដាន បញ្ជូនបន្ត)។ បញ្ចប់ បិទករណី។'],
        },
      },
    ],
  },
  {
    id: 'new-year',
    group: 'start',
    title: { en: 'Start the next academic year', km: 'ចាប់ផ្តើមឆ្នាំសិក្សាបន្ទាប់' },
    steps: [
      {
        route: 'AcademicYearPage', page: P.academicYear, target: action('create-academic-year'), check: 'nextYear',
        text: { en: 'Create next year\'s academic year. It stays a draft until the transition moves everyone into it.', km: 'បង្កើតឆ្នាំសិក្សាបន្ទាប់។ វានៅជាសេចក្តីព្រាង រហូតដល់ការផ្លាស់ប្តូរផ្ទេរគ្រប់គ្នាចូល។' },
        how: {
          en: ['Press the highlighted button: name (e.g. 2027–2028), start and end dates, term.', 'Do not run its setup wizard: the transition copies classes and periods for you.'],
          km: ['ចុចប៊ូតុងដែលភ្លឺ៖ ឈ្មោះ (ឧ. ២០២៧–២០២៨) ថ្ងៃចាប់ផ្តើម និងបញ្ចប់ និងប្រភេទឆមាស។', 'កុំដំណើរការជំនួយការរៀបចំរបស់វា៖ ការផ្លាស់ប្តូរនឹងចម្លងថ្នាក់ និងវគ្គឲ្យអ្នក។'],
        },
      },
      {
        route: 'PromotionCriteriaPage', page: P.promotion, target: action('create-promotion-criteria'), check: 'promotionCriteria',
        text: { en: 'Set when a student moves up a grade.', km: 'កំណត់លក្ខខណ្ឌដែលសិស្សឡើងថ្នាក់។' },
        how: {
          en: ['One rule per grade: from grade → to grade.', 'Minimum average score, required letter grade and minimum attendance %.', 'Auto promote: students who meet the rule move up without review.', 'Grades marked Missing criteria cannot be evaluated.'],
          km: ['ច្បាប់មួយក្នុងកម្រិតនីមួយៗ៖ ពីកម្រិត → ទៅកម្រិត។', 'ពិន្ទុមធ្យមអប្បបរមា និទ្ទេសដែលត្រូវការ និងភាគរយវត្តមានអប្បបរមា។', 'ឡើងថ្នាក់ស្វ័យប្រវត្តិ៖ សិស្សដែលបំពេញលក្ខខណ្ឌឡើងថ្នាក់ដោយមិនចាំបាច់ពិនិត្យ។', 'កម្រិតដែលសម្គាល់ថា ខ្វះលក្ខខណ្ឌ មិនអាចវាយតម្លៃបានទេ។'],
        },
      },
      {
        route: 'AcademicYearTransitionPage', alsoOn: ['AcademicYearTransitionWizardPage'], page: P.transition, check: 'transitionStarted',
        text: { en: 'Start the year transition.', km: 'ចាប់ផ្តើមការផ្លាស់ប្តូរឆ្នាំសិក្សា។' },
        how: {
          en: ['Press Create New Transition.', 'From Year: the current year. To Year: the new year.', 'Press Start Transition. A 5-stage wizard opens: Prepare, Evaluate, Review, Teacher assignments, Execute.'],
          km: ['ចុច បង្កើតការផ្លាស់ប្តូរថ្មី។', 'ពីឆ្នាំ៖ ឆ្នាំបច្ចុប្បន្ន។ ទៅឆ្នាំ៖ ឆ្នាំថ្មី។', 'ចុច ចាប់ផ្តើមការផ្លាស់ប្តូរ។ ជំនួយការ ៥ ដំណាក់កាលបើកឡើង៖ រៀបចំ វាយតម្លៃ ពិនិត្យ ការចាត់តាំងគ្រូ អនុវត្ត។'],
        },
      },
      {
        route: 'AcademicYearTransitionPage', alsoOn: ['AcademicYearTransitionWizardPage'], page: P.transition, check: 'transitionPrepared',
        text: { en: 'Prepare: copy the setup and check nothing is missing.', km: 'រៀបចំ៖ ចម្លងការរៀបចំ ហើយពិនិត្យថាគ្មានអ្វីខ្វះ។' },
        how: {
          en: ['Clone Configuration: choose what to copy. Reporting periods are copied with dates moved to the new year; check them.', 'Placement: method, maximum class size, balance by gender or score.', 'Validation: pending scores, missing reports, attendance or promotions must be fixed first.', 'Save Configuration, then Next.'],
          km: ['ចម្លងការកំណត់៖ ជ្រើសអ្វីដែលត្រូវចម្លង។ វគ្គវាយតម្លៃត្រូវចម្លង ដោយប្តូរកាលបរិច្ឆេទទៅឆ្នាំថ្មី សូមពិនិត្យ។', 'ការដាក់ថ្នាក់៖ វិធី ទំហំថ្នាក់អតិបរមា តុល្យភាពតាមភេទ ឬពិន្ទុ។', 'ការផ្ទៀងផ្ទាត់៖ ពិន្ទុរង់ចាំ របាយការណ៍ខ្វះ វត្តមាន ឬការឡើងថ្នាក់ ត្រូវកែជាមុន។', 'រក្សាទុកការកំណត់ រួច បន្ទាប់។'],
        },
      },
      {
        route: 'AcademicYearTransitionPage', alsoOn: ['AcademicYearTransitionWizardPage'], page: P.transition,
        text: { en: 'Evaluate: check every student against the promotion rules.', km: 'វាយតម្លៃ៖ ពិនិត្យសិស្សគ្រប់គ្នាតាមច្បាប់ឡើងថ្នាក់។' },
        how: {
          en: ['Give the batch a name and press Run Batch Evaluation.', 'Eligible, Not Eligible and Pending Review are counted.', 'Approve All Eligible, then approve or reject the pending ones (a reason is needed to reject).', 'Continue to Placements.'],
          km: ['ដាក់ឈ្មោះបាច់ ហើយចុច ដំណើរការវាយតម្លៃជាបាច់។', 'មានសិទ្ធិ មិនមានសិទ្ធិ និងរង់ចាំពិនិត្យ ត្រូវបានរាប់។', 'អនុម័តអ្នកមានសិទ្ធិទាំងអស់ រួចអនុម័ត ឬបដិសេធអ្នករង់ចាំ (ការបដិសេធត្រូវការមូលហេតុ)។', 'បន្តទៅការដាក់ថ្នាក់។'],
        },
      },
      {
        route: 'AcademicYearTransitionPage', alsoOn: ['AcademicYearTransitionWizardPage'], page: P.transition, check: 'transitionApproved',
        text: { en: 'Review placements and teacher assignments for the new year.', km: 'ពិនិត្យការដាក់ថ្នាក់ និងការចាត់តាំងគ្រូសម្រាប់ឆ្នាំថ្មី។' },
        how: {
          en: ['Review: Auto Distribute fills classes; fix "Class over capacity"; Edit Placement to move a student (reason optional).', 'Retained and Graduated students are listed too. Approve All, then Continue.', 'Teacher assignments: Generate, then approve, waive or change the teacher, class and course of each row. Continue.'],
          km: ['ពិនិត្យ៖ ចែកស្វ័យប្រវត្តិ បំពេញថ្នាក់ កែ "ថ្នាក់លើសចំណុះ" កែការដាក់ថ្នាក់ ដើម្បីផ្លាស់សិស្ស (មូលហេតុស្រេចចិត្ត)។', 'សិស្សត្រួតថ្នាក់ និងបញ្ចប់ការសិក្សាក៏ត្រូវបានបង្ហាញដែរ។ អនុម័តទាំងអស់ រួចបន្ត។', 'ការចាត់តាំងគ្រូ៖ ចុច បង្កើត រួចអនុម័ត លើកលែង ឬប្តូរគ្រូ ថ្នាក់ និងវគ្គសិក្សាក្នុងជួរនីមួយៗ។ បន្ត។'],
        },
      },
      {
        route: 'AcademicYearTransitionPage', alsoOn: ['AcademicYearTransitionWizardPage'], page: P.transition, check: 'transitionCompleted',
        text: { en: 'Execute: move everyone into the new year.', km: 'អនុវត្ត៖ ផ្ទេរគ្រប់គ្នាចូលឆ្នាំថ្មី។' },
        how: {
          en: ['Press Start Execution and wait. Grades, classes, courses and enrollments are created.', 'Read the Transition Summary.', 'Something wrong? Rollback undoes it; Retry runs it again.'],
          km: ['ចុច ចាប់ផ្តើមអនុវត្ត ហើយរង់ចាំ។ កម្រិតថ្នាក់ ថ្នាក់ វគ្គសិក្សា និងការចុះឈ្មោះ ត្រូវបានបង្កើត។', 'អានសេចក្តីសង្ខេបការផ្លាស់ប្តូរ។', 'មានបញ្ហា? ត្រឡប់វិញ ដើម្បីលុបចោល ព្យាយាមម្តងទៀត ដើម្បីដំណើរការម្តងទៀត។'],
        },
      },
      {
        route: 'EnrollExistingStudentPage', page: P.enrollExisting,
        text: { en: 'Enroll returning students the transition did not place.', km: 'ចុះឈ្មោះសិស្សចាស់ដែលការផ្លាស់ប្តូរមិនបានដាក់ថ្នាក់។' },
        how: {
          en: ['Find Student by name or ID.', 'Academic year, grade and class.', 'Payment option: full, partial, registration fee only or installment plan (billing frequency uses the Fee Schedule).', 'Create Enrollment, then collect the fee in Record Payment.'],
          km: ['ស្វែងរកសិស្សតាមឈ្មោះ ឬលេខសម្គាល់។', 'ឆ្នាំសិក្សា កម្រិតថ្នាក់ និងថ្នាក់។', 'ជម្រើសបង់ប្រាក់៖ បង់ពេញ បង់ខ្លះ ថ្លៃចុះឈ្មោះតែប៉ុណ្ណោះ ឬបង់រំលស់ (ប្រេកង់គិតប្រាក់ប្រើកាលវិភាគថ្លៃ)។', 'បង្កើតការចុះឈ្មោះ រួចទទួលប្រាក់នៅ កត់ត្រាការទូទាត់។'],
        },
      },
    ],
  },
  {
    id: 'calendar',
    group: 'daily',
    title: { en: 'School calendar: holidays & events', km: 'ប្រតិទិនសាលា៖ ថ្ងៃឈប់សម្រាក និងព្រឹត្តិការណ៍' },
    steps: [
      {
        route: 'AcademicCalendarPage', page: P.calendar, target: action('create-holiday'),
        text: { en: 'Add the holidays of the year.', km: 'បន្ថែមថ្ងៃឈប់សម្រាកប្រចាំឆ្នាំ។' },
        how: {
          en: ['Press Create Holiday.', 'Holiday name, date and type: national, religious or school. Add a description if useful.', 'To change one, pick its date: in the date details press Edit or Delete.'],
          km: ['ចុច បង្កើតថ្ងៃឈប់សម្រាក។', 'ឈ្មោះថ្ងៃឈប់សម្រាក កាលបរិច្ឆេទ និងប្រភេទ៖ ជាតិ សាសនា ឬសាលា។ បន្ថែមការពិពណ៌នាបើចាំបាច់។', 'ដើម្បីកែ ជ្រើសកាលបរិច្ឆេទរបស់វា៖ ក្នុងព័ត៌មានលម្អិតកាលបរិច្ឆេទ ចុច កែ ឬ លុប។'],
        },
      },
      {
        route: 'AcademicCalendarPage', page: P.calendar, target: action('create-event'),
        text: { en: 'Add school events (meetings, ceremonies, sports day…).', km: 'បន្ថែមព្រឹត្តិការណ៍សាលា (កិច្ចប្រជុំ ពិធី ទិវាកីឡា…)។' },
        how: {
          en: ['Press Create Event.', 'Event title, date, start and end time, type and location.', 'Events show on the calendar and in the date details, where you can edit or cancel them.'],
          km: ['ចុច បង្កើតព្រឹត្តិការណ៍។', 'ចំណងជើងព្រឹត្តិការណ៍ កាលបរិច្ឆេទ ម៉ោងចាប់ផ្តើម និងបញ្ចប់ ប្រភេទ និងទីតាំង។', 'ព្រឹត្តិការណ៍បង្ហាញលើប្រតិទិន និងក្នុងព័ត៌មានលម្អិតកាលបរិច្ឆេទ ដែលអ្នកអាចកែ ឬលុបចោល។'],
        },
      },
      {
        route: 'AcademicCalendarPage', page: P.calendar, target: '[data-tour="calendar-actions"]',
        text: { en: 'Add or move a single lesson by hand.', km: 'បន្ថែម ឬផ្លាស់មេរៀនមួយដោយដៃ។' },
        how: {
          en: ['Action → Create Course Schedule: academic year, grade, class, course, teacher and room.', 'Shift, day of week, start and end time. The time must be inside the shift.', 'Merge with another class: link classes taught together by the same teacher at the same time.', 'For a whole new timetable, use the Build the timetable guide.'],
          km: ['សកម្មភាព → បញ្ចូលកាលវិភាគ៖ ឆ្នាំសិក្សា កម្រិតថ្នាក់ ថ្នាក់ វគ្គសិក្សា គ្រូ និងបន្ទប់។', 'វេន ថ្ងៃក្នុងសប្តាហ៍ ម៉ោងចាប់ផ្តើម និងបញ្ចប់។ ម៉ោងត្រូវនៅក្នុងវេន។', 'បញ្ចូលជាមួយថ្នាក់ផ្សេង៖ ភ្ជាប់ថ្នាក់ដែលរៀនរួមគ្នាជាមួយគ្រូដដែល នៅម៉ោងដដែល។', 'សម្រាប់កាលវិភាគថ្មីទាំងមូល ប្រើការណែនាំ រៀបចំកាលវិភាគ។'],
        },
      },
    ],
  },
  {
    id: 'teacher-attendance',
    group: 'daily',
    title: { en: 'Teacher check-in & leave', km: 'ការចុះវត្តមាន និងច្បាប់គ្រូ' },
    steps: [
      {
        route: 'SettingPage', query: { tab: 'attendance' }, page: P.attendanceSettings,
        text: { en: 'Set the rules for teachers.', km: 'កំណត់ច្បាប់សម្រាប់គ្រូ។' },
        how: {
          en: ['Auto checkout time: teachers who forget to check out are checked out at this time.', 'Class teacher leave limit and class monitor attendance are also here. Save.'],
          km: ['ម៉ោងចេញស្វ័យប្រវត្តិ៖ គ្រូដែលភ្លេចចុះចេញ នឹងត្រូវចុះចេញនៅម៉ោងនេះ។', 'ចំនួនថ្ងៃច្បាប់របស់គ្រូប្រចាំថ្នាក់ និងវត្តមានដោយប្រធានថ្នាក់ ក៏នៅទីនេះដែរ។ រក្សាទុក។'],
        },
      },
      {
        route: 'TeacherSessionsPage', page: P.teacherSessions, target: tab('checkout_qr'),
        text: { en: 'Teachers check in on MES Mobile at school. The daily QR code is here.', km: 'គ្រូចុះវត្តមានតាម MES Mobile នៅសាលា។ កូដ QR ប្រចាំថ្ងៃនៅទីនេះ។' },
        how: {
          en: ['Open View QR Code and press Generate Daily QR.', 'Show or print it where teachers scan it; they can also enter the code.'],
          km: ['បើក មើលកូដ QR ហើយចុច បង្កើត QR ប្រចាំថ្ងៃ។', 'បង្ហាញ ឬបោះពុម្ពវានៅកន្លែងដែលគ្រូស្កេន គេក៏អាចវាយកូដបានដែរ។'],
        },
      },
      {
        route: 'TeacherSessionsPage', page: P.teacherSessions, target: tab('daily_view'),
        text: { en: 'See who checked in today.', km: 'មើលអ្នកដែលបានចុះវត្តមានថ្ងៃនេះ។' },
        how: {
          en: ['Daily View: each lesson with check-in, check-out and late minutes.', 'Session Summary: totals by grade, class or course (teaching hours).'],
          km: ['ទិដ្ឋភាពប្រចាំថ្ងៃ៖ មេរៀននីមួយៗ ជាមួយម៉ោងចូល ម៉ោងចេញ និងនាទីយឺត។', 'សេចក្តីសង្ខេបវគ្គ៖ សរុបតាមកម្រិតថ្នាក់ ថ្នាក់ ឬវគ្គសិក្សា (ម៉ោងបង្រៀន)។'],
        },
      },
      {
        route: 'TeacherSessionsPage', page: P.teacherSessions, target: tab('late_approvals'),
        text: { en: 'Approve or reject very late check-ins.', km: 'អនុម័ត ឬបដិសេធការចុះវត្តមានយឺតខ្លាំង។' },
        how: {
          en: ['Check-ins after the allowed late limit wait here.', 'Approve, or Reject late check-in with a reason.'],
          km: ['ការចុះវត្តមានក្រោយពេលយឺតដែលអនុញ្ញាត រង់ចាំនៅទីនេះ។', 'អនុម័ត ឬ បដិសេធការចុះវត្តមានយឺត ជាមួយមូលហេតុ។'],
        },
      },
      {
        route: 'TeacherSessionsPage', page: P.teacherSessions, target: tab('request_permission'),
        text: { en: 'Handle teachers\' leave requests.', km: 'ដោះស្រាយសំណើច្បាប់របស់គ្រូ។' },
        how: {
          en: ['Teachers request leave in MES Mobile.', 'Check the dates and reason, then approve or reject.'],
          km: ['គ្រូស្នើសុំច្បាប់ក្នុង MES Mobile។', 'ពិនិត្យកាលបរិច្ឆេទ និងមូលហេតុ រួចអនុម័ត ឬបដិសេធ។'],
        },
      },
    ],
  },
  {
    id: 'teacher-availability',
    group: 'teaching',
    title: { en: 'Teacher availability', km: 'ពេលទំនេររបស់គ្រូ' },
    steps: [
      {
        route: 'TeacherAvailabilitySettingsPage', page: P.availabilitySettings,
        text: { en: 'Decide whether teachers may send their own free time.', km: 'សម្រេចថាគ្រូអាចផ្ញើពេលទំនេររបស់ខ្លួនឬអត់។' },
        how: {
          en: ['Pick the branch, then Enabled or Disabled.', 'Enabled: teachers submit during an open collection round. Disabled: only administrators manage it.', 'Apply changes to all active branches if they share the rule. Save.'],
          km: ['ជ្រើសសាខា រួច បើក ឬ បិទ។', 'បើក៖ គ្រូដាក់ស្នើក្នុងវគ្គប្រមូលដែលបើក។ បិទ៖ មានតែអ្នកគ្រប់គ្រងទេដែលគ្រប់គ្រង។', 'អនុវត្តការផ្លាស់ប្តូរទៅគ្រប់សាខាសកម្ម បើប្រើច្បាប់ដូចគ្នា។ រក្សាទុក។'],
        },
      },
      {
        route: 'TeacherAvailabilityPage', page: P.availability,
        text: { en: 'Set a teacher\'s regular weekly free time yourself.', km: 'កំណត់ពេលទំនេរប្រចាំសប្តាហ៍របស់គ្រូដោយខ្លួនឯង។' },
        how: {
          en: ['Search the teacher in the picker.', 'Add Time Slot: day of week, start and end time. Mark preferred times.', 'Analytics shows total hours and days available.'],
          km: ['ស្វែងរកគ្រូក្នុងប្រអប់ជ្រើស។', 'បន្ថែមម៉ោង៖ ថ្ងៃក្នុងសប្តាហ៍ ម៉ោងចាប់ផ្តើម និងបញ្ចប់។ សម្គាល់ម៉ោងដែលចូលចិត្ត។', 'ការវិភាគបង្ហាញម៉ោងសរុប និងថ្ងៃទំនេរ។'],
        },
      },
      {
        route: 'TeacherAvailabilityApprovalPage', page: P.availabilityApproval, target: tab('pending'),
        text: { en: 'Approve availability changes teachers send.', km: 'អនុម័តការផ្លាស់ប្តូរពេលទំនេរដែលគ្រូផ្ញើ។' },
        how: {
          en: ['Pending: review each request and approve or reject it.', 'Approved and Rejected keep the history.'],
          km: ['រង់ចាំ៖ ពិនិត្យសំណើនីមួយៗ ហើយអនុម័ត ឬបដិសេធ។', 'បានអនុម័ត និងបានបដិសេធ រក្សាប្រវត្តិ។'],
        },
      },
    ],
  },
  {
    id: 'student-records',
    group: 'people',
    title: { en: 'Transfers, ID cards & parents', km: 'ការផ្ទេរ បណ្ណសម្គាល់ និងអាណាព្យាបាល' },
    steps: [
      {
        route: 'StudentTransfersPage', page: P.transfers,
        text: { en: 'Move a student to another class or school.', km: 'ផ្ទេរសិស្សទៅថ្នាក់ ឬសាលាផ្សេង។' },
        how: {
          en: ['Create transfer: pick the student (needs an active enrollment).', 'Type: move to another class, transfer to an MES school, or withdraw to an external school. Choose the destination and a reason.', 'Incoming MES transfers: accept (choose class and year) or reject transfers sent to you. Then map their academic summaries.'],
          km: ['បង្កើតការផ្ទេរ៖ ជ្រើសសិស្ស (ត្រូវមានការចុះឈ្មោះសកម្ម)។', 'ប្រភេទ៖ ផ្លាស់ទៅថ្នាក់ផ្សេង ផ្ទេរទៅសាលា MES ឬដកចេញទៅសាលាខាងក្រៅ។ ជ្រើសគោលដៅ និងមូលហេតុ។', 'ការផ្ទេរចូល MES៖ ទទួលយក (ជ្រើសថ្នាក់ និងឆ្នាំ) ឬបដិសេធការផ្ទេរដែលផ្ញើមកអ្នក។ រួចផ្គូផ្គងសេចក្តីសង្ខេបការសិក្សា។'],
        },
      },
      {
        route: 'SchoolIdCardsPage', query: { tab: 'student' }, page: P.idCards, target: '[data-tour="id-cards-load"]',
        text: { en: 'Print student ID cards.', km: 'បោះពុម្ពបណ្ណសម្គាល់សិស្ស។' },
        how: {
          en: ['Choose grade and class, then Load students.', 'Select the students (or Select all).', 'Preview and print.'],
          km: ['ជ្រើសកម្រិតថ្នាក់ និងថ្នាក់ រួច ផ្ទុកសិស្ស។', 'ជ្រើសសិស្ស (ឬ ជ្រើសទាំងអស់)។', 'មើលជាមុន និងបោះពុម្ព។'],
        },
      },
      {
        route: 'ParentsPage', page: P.parents, target: '[data-tour="table-actions"]', check: 'parent',
        text: { en: 'Manage parents and their logins.', km: 'គ្រប់គ្រងអាណាព្យាបាល និងគណនីរបស់គេ។' },
        how: {
          en: ['Action → Create Parent, or Import parent by Excel (download the template first).', 'Row menu: Change Phone number (the username changes too) or Change Children.', 'Parents use MES Mobile for fees, leave, attendance and results.'],
          km: ['សកម្មភាព → បង្កើតអាណាព្យាបាល ឬ នាំចូលអាណាព្យាបាលដោយ Excel (ទាញយកគំរូជាមុន)។', 'ម៉ឺនុយជួរ៖ ប្តូរលេខទូរស័ព្ទ (ឈ្មោះអ្នកប្រើក៏ប្តូរដែរ) ឬ ប្តូរកូន។', 'អាណាព្យាបាលប្រើ MES Mobile សម្រាប់កម្រៃ ច្បាប់ វត្តមាន និងលទ្ធផល។'],
        },
      },
    ],
  },
  {
    id: 'staff',
    group: 'people',
    title: { en: 'Staff, roles & permissions', km: 'បុគ្គលិក តួនាទី និងសិទ្ធិ' },
    steps: [
      {
        route: 'StaffPage', page: P.staff, target: action('create-staff'), check: 'staff',
        text: { en: 'Add office staff who use the back office.', km: 'បន្ថែមបុគ្គលិកការិយាល័យដែលប្រើប្រព័ន្ធគ្រប់គ្រង។' },
        how: {
          en: ['Press Create staff: name, email, phone, position, department and branch.', 'Give them a role so they only see what they need.', 'Row menu: Edit, Reset Password or Archive.'],
          km: ['ចុច បង្កើតបុគ្គលិក៖ ឈ្មោះ អ៊ីមែល ទូរស័ព្ទ មុខតំណែង នាយកដ្ឋាន និងសាខា។', 'ផ្តល់តួនាទី ដើម្បីឲ្យគេឃើញតែអ្វីដែលត្រូវការ។', 'ម៉ឺនុយជួរ៖ កែប្រែ កំណត់ពាក្យសម្ងាត់ឡើងវិញ ឬ រក្សាទុកក្នុងបណ្ណសារ។'],
        },
      },
      {
        route: 'SettingPage', query: { tab: 'users' }, page: P.usersSettings,
        text: { en: 'Choose what each role may see and do.', km: 'ជ្រើសអ្វីដែលតួនាទីនីមួយៗអាចមើល និងធ្វើ។' },
        how: {
          en: ['Roles & Permissions: pick a role (Administrator, Staff, Teacher…) or Add new role.', 'Edit Permissions: base and granular permissions, then Save Changes.', 'Users: search a user to change their role or reset the password.'],
          km: ['តួនាទី និងសិទ្ធិ៖ ជ្រើសតួនាទី (អ្នកគ្រប់គ្រង បុគ្គលិក គ្រូ…) ឬ បន្ថែមតួនាទីថ្មី។', 'កែសិទ្ធិ៖ សិទ្ធិមូលដ្ឋាន និងសិទ្ធិលម្អិត រួច រក្សាទុកការផ្លាស់ប្តូរ។', 'អ្នកប្រើ៖ ស្វែងរកអ្នកប្រើ ដើម្បីប្តូរតួនាទី ឬកំណត់ពាក្យសម្ងាត់ឡើងវិញ។'],
        },
      },
    ],
  },
  {
    id: 'grading',
    group: 'teaching',
    title: { en: 'Letter grades & report results', km: 'និទ្ទេស និងលទ្ធផលរបាយការណ៍' },
    steps: [
      {
        route: 'LetterGradePage', page: P.letterGrade, target: action('auto-create-grading-scales'), check: 'letterGrade',
        text: { en: 'Set the letter grades (A, B, C…) for scores.', km: 'កំណត់និទ្ទេស (A B C…) សម្រាប់ពិន្ទុ។' },
        how: {
          en: ['Auto-create grading scales makes a standard scale for you, or Create letter grade one by one.', 'Each grade has a score range and points; scales can differ for courses, groups and averages.'],
          km: ['បង្កើតមាត្រដ្ឋានដោយស្វ័យប្រវត្តិ បង្កើតមាត្រដ្ឋានស្តង់ដារឲ្យអ្នក ឬ បង្កើតនិទ្ទេស ម្តងមួយៗ។', 'និទ្ទេសនីមួយៗមានចន្លោះពិន្ទុ និងពិន្ទុគុណ មាត្រដ្ឋានអាចខុសគ្នាសម្រាប់វគ្គសិក្សា ក្រុម និងមធ្យមភាគ។'],
        },
      },
      {
        route: 'ReportCalculationPolicyPage', page: P.calcPolicy, check: 'policySaved', optional: true,
        text: { en: 'Choose how semester results are calculated. Optional: without a policy, a semester is 50% the average of its monthly results + 50% its semester exam.', km: 'ជ្រើសរបៀបគណនាលទ្ធផលឆមាស។ ស្រេចចិត្ត៖ បើគ្មានគោលការណ៍ ឆមាសគឺ ៥០% មធ្យមភាគលទ្ធផលប្រចាំខែ + ៥០% ប្រឡងឆមាស។' },
        how: {
          en: ['Builder: Name (e.g. Semester policy 2026), Target period: Semester, Mode: Guided.', 'Scope: School default, or Branch override for one branch.', 'Monthly children: monthly results roll into the semester. Direct semester score: the semester exam counts too.', 'Aggregation: Average or Weighted total, then the Monthly weight and Direct score weight (e.g. 40 and 60).', 'Show preview to see the effect, Validate, then Save draft.'],
          km: ['ឧបករណ៍បង្កើត៖ ឈ្មោះ (ឧ. គោលការណ៍ឆមាស ២០២៦) វគ្គគោលដៅ៖ ឆមាស របៀប៖ មានការណែនាំ។', 'វិសាលភាព៖ លំនាំដើមសាលា ឬ ការកំណត់សាខា សម្រាប់សាខាមួយ។', 'វគ្គប្រចាំខែ៖ លទ្ធផលប្រចាំខែរាប់បញ្ចូលក្នុងឆមាស។ ពិន្ទុឆមាសផ្ទាល់៖ ប្រឡងឆមាសក៏រាប់ដែរ។', 'ការបូកសរុប៖ មធ្យមភាគ ឬ សរុបតាមទម្ងន់ រួចទម្ងន់ប្រចាំខែ និងទម្ងន់ពិន្ទុផ្ទាល់ (ឧ. ៤០ និង ៦០)។', 'បង្ហាញការមើលជាមុន ដើម្បីមើលលទ្ធផល ផ្ទៀងផ្ទាត់ រួច រក្សាទុកសេចក្តីព្រាង។'],
        },
      },
      {
        route: 'ReportCalculationPolicyPage', page: P.calcPolicy, check: 'policyActive', optional: true,
        text: { en: 'Publish the policy so report cards use it.', km: 'ផ្សព្វផ្សាយគោលការណ៍ ដើម្បីឲ្យសៀវភៅតាមដានប្រើវា។' },
        how: {
          en: ['In Policies, find your draft and press Publish. Its status becomes active.', 'Yearly results: make another policy with Target period: Yearly; it uses each semester\'s result after its policy.', 'Archive a policy to stop using it; the default applies again.', 'Advanced mode builds the formula from nodes on a canvas (children, average, weighted mix) with an output node.'],
          km: ['នៅក្នុង គោលការណ៍ រកសេចក្តីព្រាងរបស់អ្នក ហើយចុច ផ្សព្វផ្សាយ។ ស្ថានភាពក្លាយជាសកម្ម។', 'លទ្ធផលប្រចាំឆ្នាំ៖ បង្កើតគោលការណ៍មួយទៀត ជាមួយវគ្គគោលដៅ៖ ប្រចាំឆ្នាំ វាប្រើលទ្ធផលឆមាសនីមួយៗក្រោយអនុវត្តគោលការណ៍របស់វា។', 'រក្សាទុកក្នុងបណ្ណសារ ដើម្បីឈប់ប្រើគោលការណ៍ លំនាំដើមនឹងអនុវត្តវិញ។', 'របៀបកម្រិតខ្ពស់ បង្កើតរូបមន្តពីថ្នាំងលើផ្ទាំង (វគ្គកូន មធ្យមភាគ ការលាយតាមទម្ងន់) ជាមួយថ្នាំងលទ្ធផល។'],
        },
      },
      {
        route: 'ClassCourseReportPage', page: P.classCourseReport,
        text: { en: 'See one class\'s results subject by subject.', km: 'មើលលទ្ធផលថ្នាក់មួយតាមមុខវិជ្ជានីមួយៗ។' },
        how: {
          en: ['Pick the class and period, then View Report.', 'Move between courses with Previous and Next.'],
          km: ['ជ្រើសថ្នាក់ និងវគ្គ រួច មើលរបាយការណ៍។', 'ផ្លាស់ទីរវាងវគ្គសិក្សាដោយ មុន និង បន្ទាប់។'],
        },
      },
      {
        route: 'LeaderboardPage', page: P.leaderboard,
        text: { en: 'Print the honor board of top students.', km: 'បោះពុម្ពតារាងកិត្តិយសសិស្សពូកែ។' },
        how: {
          en: ['Choose the reporting period and class, Overall or By Course, then View Report.', 'Choose a style and photo proportions; edit the header and signatures.', 'Print.'],
          km: ['ជ្រើសវគ្គវាយតម្លៃ និងថ្នាក់ ទាំងមូល ឬ តាមវគ្គសិក្សា រួច មើលរបាយការណ៍។', 'ជ្រើសរចនាប័ទ្ម និងសមាមាត្ររូបថត កែក្បាល និងហត្ថលេខា។', 'បោះពុម្ព។'],
        },
      },
    ],
  },
  {
    id: 'homework-quiz',
    group: 'teaching',
    title: { en: 'Homework, quizzes & anonymous exams', km: 'កិច្ចការផ្ទះ ការប្រលងសាកល្បង និងការប្រឡងអនាមិក' },
    steps: [
      {
        route: 'HomeworkPage', page: P.homework, target: action('create-homework'), doneWhen: '[data-guide="homework-exists"]',
        text: { en: 'Give homework and grade it.', km: 'ដាក់កិច្ចការផ្ទះ ហើយដាក់ពិន្ទុ។' },
        how: {
          en: ['Create Homework: title, description, academic year, grade, class, course, due date and max score. Link it to an exam to count its score.', 'Students hand it in on MES Mobile.', 'Grading Queue: grade each submission. Analytics shows completion and on-time rates.'],
          km: ['បង្កើតកិច្ចការផ្ទះ៖ ចំណងជើង ការពិពណ៌នា ឆ្នាំសិក្សា កម្រិតថ្នាក់ ថ្នាក់ វគ្គសិក្សា ថ្ងៃផុតកំណត់ និងពិន្ទុអតិបរមា។ ភ្ជាប់ទៅការប្រឡង ដើម្បីរាប់ពិន្ទុ។', 'សិស្សប្រគល់តាម MES Mobile។', 'ជួរដាក់ពិន្ទុ៖ ដាក់ពិន្ទុការប្រគល់នីមួយៗ។ ការវិភាគបង្ហាញអត្រាបញ្ចប់ និងទាន់ពេល។'],
        },
      },
      {
        route: 'QuizPage', page: P.quiz, target: action('create-quiz'), check: 'quiz',
        text: { en: 'Make quizzes for a class.', km: 'បង្កើតការប្រលងសាកល្បងសម្រាប់ថ្នាក់។' },
        how: {
          en: ['Create Quiz: title, quiz type (multiple choice, true/false, short answer, essay, mixed or Google Form), grade, class and course.', 'Settings: duration, total points, passing score, start and end dates, max attempts.', 'Google Form: generate a new form automatically, or link an existing one; responses and grades sync by themselves.', 'Analytics: completion rate, average score and attempts.'],
          km: ['បង្កើតការប្រលងសាកល្បង៖ ចំណងជើង ប្រភេទ (ពហុជម្រើស ត្រូវ/ខុស ចម្លើយខ្លី អត្ថបទ ចម្រុះ ឬ Google Form) កម្រិតថ្នាក់ ថ្នាក់ និងវគ្គសិក្សា។', 'ការកំណត់៖ រយៈពេល ពិន្ទុសរុប ពិន្ទុជាប់ ថ្ងៃចាប់ផ្តើម និងបញ្ចប់ ចំនួនដងព្យាយាមអតិបរមា។', 'Google Form៖ បង្កើតទម្រង់ថ្មីដោយស្វ័យប្រវត្តិ ឬភ្ជាប់ទម្រង់ដែលមានស្រាប់ ចម្លើយ និងពិន្ទុធ្វើសមកាលកម្មដោយខ្លួនឯង។', 'ការវិភាគ៖ អត្រាបញ្ចប់ ពិន្ទុមធ្យម និងចំនួនការព្យាយាម។'],
        },
      },
      {
        route: 'AnonymousExamPage', page: P.anonymousExam,
        text: { en: 'Grade an exam blind: teachers see codes, not names.', km: 'ដាក់ពិន្ទុការប្រឡងដោយមិនឃើញឈ្មោះ៖ គ្រូឃើញតែកូដ។' },
        how: {
          en: ['Pick the exam and Load. Generate Anonymous Codes (Generate Missing Codes for new students).', 'Print Coupon Sheet and give each student their code.', 'Blind Grading: enter scores by code, then Save Scores.', 'Reveal Scores locks them and sends them to the normal score approval.'],
          km: ['ជ្រើសការប្រឡង ហើយ ផ្ទុក។ បង្កើតកូដអនាមិក (បង្កើតកូដដែលខ្វះ សម្រាប់សិស្សថ្មី)។', 'បោះពុម្ពសន្លឹកប័ណ្ណ ហើយឲ្យកូដដល់សិស្សម្នាក់ៗ។', 'ដាក់ពិន្ទុដោយមិនឃើញឈ្មោះ៖ បញ្ចូលពិន្ទុតាមកូដ រួច រក្សាទុកពិន្ទុ។', 'បង្ហាញពិន្ទុ ចាក់សោវា ហើយផ្ញើទៅការអនុម័តពិន្ទុធម្មតា។'],
        },
      },
    ],
  },
  {
    id: 'transport',
    group: 'more',
    title: { en: 'School transport', km: 'ការដឹកជញ្ជូនសាលា' },
    steps: [
      {
        route: 'DriversPage', page: P.drivers, target: '[data-tour="drivers-add"]', check: 'driver',
        text: { en: 'Add your drivers. Each gets a login for the Driver app.', km: 'បន្ថែមអ្នកបើកបរ។ ម្នាក់ៗទទួលបានគណនីសម្រាប់កម្មវិធីអ្នកបើកបរ។' },
        how: {
          en: ['Add Driver: name, username and password, phone, emergency phone and branch.', 'Record the licence number and expiry.'],
          km: ['បន្ថែមអ្នកបើកបរ៖ ឈ្មោះ ឈ្មោះអ្នកប្រើ និងពាក្យសម្ងាត់ ទូរស័ព្ទ ទូរស័ព្ទបន្ទាន់ និងសាខា។', 'កត់ត្រាលេខប័ណ្ណបើកបរ និងថ្ងៃផុតកំណត់។'],
        },
      },
      {
        route: 'TransportManagementPage', page: P.transport, target: '[data-tour="transport-new-route"]', check: 'route',
        text: { en: 'Create routes with their stops and fares.', km: 'បង្កើតផ្លូវ ជាមួយចំណត និងតម្លៃ។' },
        how: {
          en: ['New Route: service (morning pickup or afternoon drop-off), capacity, vehicle and default driver.', 'Add Stop: each stop in order, with its fare.', 'Fleet lists your vehicles with seats and inspection dates.'],
          km: ['ផ្លូវថ្មី៖ សេវា (ទទួលពេលព្រឹក ឬជូនពេលរសៀល) ចំណុះ យានជំនិះ និងអ្នកបើកបរលំនាំដើម។', 'បន្ថែមចំណត៖ ចំណតនីមួយៗតាមលំដាប់ ជាមួយតម្លៃ។', 'ក្រុមយានជំនិះ បង្ហាញយានជំនិះ ចំនួនកៅអី និងថ្ងៃត្រួតពិនិត្យ។'],
        },
      },
      {
        route: 'TransportManagementPage', page: P.transport, target: '[data-tour="transport-register-student"]', check: 'transportStudent',
        text: { en: 'Register students for transport.', km: 'ចុះឈ្មោះសិស្សជិះឡានសាលា។' },
        how: {
          en: ['Register Student: search the student, pick the route and stop.', 'The stop\'s fare becomes their transport fee; seats are checked when you save.'],
          km: ['ចុះឈ្មោះសិស្ស៖ ស្វែងរកសិស្ស ជ្រើសផ្លូវ និងចំណត។', 'តម្លៃនៃចំណតក្លាយជាថ្លៃដឹកជញ្ជូនរបស់គេ កៅអីត្រូវពិនិត្យពេលរក្សាទុក។'],
        },
      },
      {
        route: 'TransportStudentPassesPage', page: P.passes,
        text: { en: 'Decide who may collect each child.', km: 'សម្រេចថាអ្នកណាអាចមកទទួលកូនម្នាក់ៗ។' },
        how: {
          en: ['Pickup Contacts: add parents or other approved people (name, relationship, phone).', 'Only approved, active contacts can complete a safe handoff.', 'End service stops a student\'s transport (with a reason); trip records stay.'],
          km: ['អ្នកទទួល៖ បន្ថែមអាណាព្យាបាល ឬមនុស្សផ្សេងដែលបានអនុម័ត (ឈ្មោះ ទំនាក់ទំនង ទូរស័ព្ទ)។', 'មានតែអ្នកទំនាក់ទំនងដែលបានអនុម័ត និងសកម្មទេ ទើបអាចទទួលកូនបាន។', 'បញ្ឈប់សេវា បញ្ចប់ការដឹកជញ្ជូនរបស់សិស្ស (ជាមួយមូលហេតុ) កំណត់ត្រាដំណើរនៅដដែល។'],
        },
      },
      {
        route: 'TransportTripsPage', page: P.trips,
        text: { en: 'Check each day\'s trips before service starts.', km: 'ពិនិត្យដំណើរប្រចាំថ្ងៃ មុនសេវាចាប់ផ្តើម។' },
        how: {
          en: ['Trips for tomorrow are made every night. Today / Tomorrow switches the day.', 'Assign Driver or Replace Driver, and Assign Attendants.', 'View manifest shows the students on each trip. Generate now if trips are missing.'],
          km: ['ដំណើរសម្រាប់ថ្ងៃស្អែកត្រូវបង្កើតរៀងរាល់យប់។ ថ្ងៃនេះ / ថ្ងៃស្អែក ប្តូរថ្ងៃ។', 'ចាត់តាំងអ្នកបើកបរ ឬ ប្តូរអ្នកបើកបរ និង ចាត់តាំងអ្នកជួយ។', 'មើលបញ្ជីបង្ហាញសិស្សលើដំណើរនីមួយៗ។ បង្កើតឥឡូវ បើដំណើរខ្វះ។'],
        },
      },
      {
        route: 'TransportLiveTrackingPage', page: P.liveTracking,
        text: { en: 'Follow buses live. Parents see their child\'s bus in MES Mobile too.', km: 'តាមដានឡានផ្ទាល់។ អាណាព្យាបាលក៏ឃើញឡានកូនរបស់គេក្នុង MES Mobile ដែរ។' },
        how: {
          en: ['A trip appears once its driver starts it in the Driver app.'],
          km: ['ដំណើរលេចឡើង ពេលអ្នកបើកបរចាប់ផ្តើមវាក្នុងកម្មវិធីអ្នកបើកបរ។'],
        },
      },
      {
        route: 'TransportReportsPage', page: P.transportReports,
        text: { en: 'Review service and money.', km: 'ពិនិត្យសេវា និងប្រាក់។' },
        how: {
          en: ['Choose a date range and Apply range.', 'Trips completed, delays, passenger outcomes and route performance.', 'Transport fee reconciliation: billed, collected and outstanding.'],
          km: ['ជ្រើសចន្លោះកាលបរិច្ឆេទ ហើយ អនុវត្ត។', 'ដំណើរបានបញ្ចប់ ការយឺត លទ្ធផលអ្នកដំណើរ និងប្រសិទ្ធភាពផ្លូវ។', 'ការផ្ទៀងផ្ទាត់ថ្លៃដឹកជញ្ជូន៖ បានចេញវិក្កយបត្រ បានប្រមូល និងនៅជំពាក់។'],
        },
      },
    ],
  },
  {
    id: 'fees-billing',
    group: 'money',
    title: { en: 'Fees, invoices & refunds', km: 'កម្រៃ វិក្កយបត្រ និងការសងប្រាក់វិញ' },
    steps: [
      {
        route: 'FeeSchedulesPage', page: P.feeSchedules, target: action('add-fee-schedule'), check: 'feeSchedule',
        text: { en: 'Set when installments are due.', km: 'កំណត់ពេលត្រូវបង់រំលស់។' },
        how: {
          en: ['Add: name, academic year and frequency (monthly, term…).', 'Due date rule: a fixed day of the month for everyone, or each student\'s enrollment day.', 'Number of installments and payment day (up to 28).'],
          km: ['បន្ថែម៖ ឈ្មោះ ឆ្នាំសិក្សា និងប្រេកង់ (ប្រចាំខែ ឆមាស…)។', 'ច្បាប់ថ្ងៃផុតកំណត់៖ ថ្ងៃថេរក្នុងខែសម្រាប់គ្រប់គ្នា ឬថ្ងៃចុះឈ្មោះរបស់សិស្សម្នាក់ៗ។', 'ចំនួនដងបង់ និងថ្ងៃបង់ (រហូតដល់ ២៨)។'],
        },
      },
      {
        route: 'RecordPaymentPage', page: P.recordPayment,
        text: { en: 'Collect monthly or installment payments.', km: 'ទទួលការបង់ប្រាក់ប្រចាំខែ ឬបង់រំលស់។' },
        how: {
          en: ['Search the student by name or ID and pick the enrollment.', 'Check the amount due now.', 'Choose the method and amount, then record it. A receipt is issued and the parent sees it in MES Mobile.'],
          km: ['ស្វែងរកសិស្សតាមឈ្មោះ ឬលេខសម្គាល់ ហើយជ្រើសការចុះឈ្មោះ។', 'ពិនិត្យចំនួនដែលត្រូវបង់ឥឡូវ។', 'ជ្រើសវិធីបង់ និងចំនួន រួចកត់ត្រា។ បង្កាន់ដៃនឹងចេញ ហើយអាណាព្យាបាលឃើញក្នុង MES Mobile។'],
        },
      },
      {
        route: 'KHQRSettingsPage', page: P.paymentMethods,
        text: { en: 'Choose how parents can pay.', km: 'ជ្រើសរបៀបដែលអាណាព្យាបាលអាចបង់។' },
        how: {
          en: ['Turn on the payment methods you accept.', 'KHQR: add an account (merchant name, account ID), then paste the KHQR string or upload the QR image.', 'ABA: set the Telegram group for payment verification.'],
          km: ['បើកវិធីបង់ប្រាក់ដែលអ្នកទទួល។', 'KHQR៖ បន្ថែមគណនី (ឈ្មោះអាជីវករ លេខគណនី) រួចបិទភ្ជាប់ខ្សែអក្សរ KHQR ឬផ្ទុករូប QR។', 'ABA៖ កំណត់ក្រុម Telegram សម្រាប់ផ្ទៀងផ្ទាត់ការបង់ប្រាក់។'],
        },
      },
      {
        route: 'EnrollmentSettingsPage', page: P.enrollmentSettings,
        text: { en: 'Decide how parents enroll and pay online.', km: 'សម្រេចរបៀបដែលអាណាព្យាបាលចុះឈ្មោះ និងបង់ប្រាក់អនឡាញ។' },
        how: {
          en: ['Self-service: self-registration, self-enrollment, admin approval and waitlist.', 'Payment: allow installment plans or require full payment.', 'Registration status (open, closed, waitlist only) with open and close dates, and notifications. Save Settings.'],
          km: ['សេវាខ្លួនឯង៖ ចុះឈ្មោះដោយខ្លួនឯង ចុះឈ្មោះចូលរៀនដោយខ្លួនឯង ការអនុម័តរបស់អ្នកគ្រប់គ្រង និងបញ្ជីរង់ចាំ។', 'ការបង់ប្រាក់៖ អនុញ្ញាតបង់រំលស់ ឬតម្រូវឲ្យបង់ពេញ។', 'ស្ថានភាពចុះឈ្មោះ (បើក បិទ បញ្ជីរង់ចាំតែប៉ុណ្ណោះ) ជាមួយថ្ងៃបើក និងបិទ និងការជូនដំណឹង។ រក្សាទុកការកំណត់។'],
        },
      },
      {
        route: 'ArDocumentsPage', page: P.invoices,
        text: { en: 'Check who owes what.', km: 'ពិនិត្យថាអ្នកណាជំពាក់អ្វី។' },
        how: {
          en: ['Pick a student or family to see their statement: opening balance, invoices, receipts, closing balance.', 'Outstanding lists unpaid invoices; currencies are never added together.', 'Write-offs: approve or reject requests. Print any invoice or receipt.'],
          km: ['ជ្រើសសិស្ស ឬគ្រួសារ ដើម្បីមើលរបាយការណ៍៖ សមតុល្យដើម វិក្កយបត្រ បង្កាន់ដៃ សមតុល្យចុង។', 'នៅជំពាក់ បង្ហាញវិក្កយបត្រមិនទាន់បង់ រូបិយប័ណ្ណមិនត្រូវបូកបញ្ចូលគ្នាទេ។', 'ការលុបបំណុល៖ អនុម័ត ឬបដិសេធសំណើ។ បោះពុម្ពវិក្កយបត្រ ឬបង្កាន់ដៃណាមួយ។'],
        },
      },
      {
        route: 'ManageRefundsPage', page: P.refunds,
        text: { en: 'Cancel an admission fee or refund a payment.', km: 'លុបថ្លៃចូលរៀន ឬសងប្រាក់វិញ។' },
        how: {
          en: ['Enter the payment ID and Load.', 'Cancel the fee, or for ABA PayWay payments send a refund.', 'A reason is required.'],
          km: ['បញ្ចូលលេខសម្គាល់ការបង់ប្រាក់ ហើយ ផ្ទុក។', 'លុបថ្លៃ ឬសម្រាប់ការបង់តាម ABA PayWay ផ្ញើការសងប្រាក់វិញ។', 'ត្រូវការមូលហេតុ។'],
        },
      },
      {
        route: 'MESSettlementPage', page: P.settlement,
        text: { en: 'Match online payments MES collected with what was paid out to your bank.', km: 'ផ្គូផ្គងការបង់ប្រាក់អនឡាញដែល MES ប្រមូល ជាមួយប្រាក់ដែលបានផ្ទេរទៅធនាគាររបស់អ្នក។' },
        how: {
          en: ['Received, Settled and Outstanding totals for the chosen dates.', 'Open items list payments not paid out yet.'],
          km: ['សរុប បានទទួល បានទូទាត់ និងនៅជំពាក់ សម្រាប់កាលបរិច្ឆេទដែលជ្រើស។', 'ធាតុបើកចំហ បង្ហាញការបង់ប្រាក់ដែលមិនទាន់ផ្ទេរ។'],
        },
      },
    ],
  },
  {
    id: 'fee-follow-up',
    group: 'money',
    title: { en: 'Follow up unpaid fees', km: 'តាមដានថ្លៃមិនទាន់បង់' },
    steps: [
      {
        route: 'CollectionFollowUpPage', page: P.feeFollowUp, target: action('reconcile'),
        text: { en: 'See every unpaid invoice as a case.', km: 'មើលវិក្កយបត្រមិនទាន់បង់នីមួយៗជាករណី។' },
        how: {
          en: ['A case opens for each issued invoice with money owed, and closes by itself when it is paid, written off or voided.', 'Needs review: the invoice is a draft or its accounting does not add up yet.', 'Payments update cases automatically; press Reconcile balances to update them now.', 'Advanced filters: Overdue shows cases past their next contact, promise or invoice due date; Mine / Unassigned shows your cases.'],
          km: ['ករណីបើកសម្រាប់វិក្កយបត្រដែលបានចេញ ហើយនៅជំពាក់ និងបិទដោយខ្លួនឯង ពេលបង់រួច លុបបំណុល ឬទុកជាមោឃៈ។', 'ត្រូវពិនិត្យ៖ វិក្កយបត្រនៅជាសេចក្តីព្រាង ឬគណនេយ្យមិនទាន់ត្រូវគ្នា។', 'ការបង់ប្រាក់ធ្វើបច្ចុប្បន្នភាពករណីដោយស្វ័យប្រវត្តិ ចុច ផ្ទៀងផ្ទាត់សមតុល្យ ដើម្បីធ្វើបច្ចុប្បន្នភាពឥឡូវ។', 'តម្រងកម្រិតខ្ពស់៖ ហួសកំណត់ បង្ហាញករណីដែលហួសថ្ងៃទំនាក់ទំនងបន្ទាប់ ថ្ងៃសន្យា ឬថ្ងៃផុតកំណត់វិក្កយបត្រ ខ្ញុំទទួលបន្ទុក / គ្មានអ្នកទទួលបន្ទុក បង្ហាញករណីរបស់អ្នក។'],
        },
      },
      {
        route: 'CollectionFollowUpPage', page: P.feeFollowUp,
        text: { en: 'Contact the family and record what they said.', km: 'ទាក់ទងគ្រួសារ ហើយកត់ត្រាអ្វីដែលគេបាននិយាយ។' },
        how: {
          en: ['View case, then Assign owner to choose the collector.', 'Log contact with a note and the next contact date.', 'Payment promise: the date the family promised to pay.', 'Pause stops follow-up for now (a note is required); Resume restarts it.'],
          km: ['មើលករណី រួច កំណត់អ្នកទទួលបន្ទុក ដើម្បីជ្រើសអ្នកប្រមូលប្រាក់។', 'កត់ទំនាក់ទំនង ជាមួយកំណត់ចំណាំ និងកាលបរិច្ឆេទទំនាក់ទំនងបន្ទាប់។', 'ការសន្យាបង់៖ កាលបរិច្ឆេទដែលគ្រួសារសន្យាថានឹងបង់។', 'ផ្អាក បញ្ឈប់ការតាមដានមួយរយៈ (ត្រូវការកំណត់ចំណាំ) បន្ត ចាប់ផ្តើមវិញ។'],
        },
      },
      {
        route: 'CollectionFollowUpPage', page: P.feeFollowUp,
        text: { en: 'Send the parents a reminder.', km: 'ផ្ញើការរំលឹកទៅអាណាព្យាបាល។' },
        how: {
          en: ['In the case, preview the reminder: the invoice and which parents will receive it.', 'Send it. Parents get a notification that opens the invoice in MES Mobile.', 'Each parent gets at most one reminder per invoice every 24 hours, and none once the invoice is paid.'],
          km: ['ក្នុងករណី មើលការរំលឹកជាមុន៖ វិក្កយបត្រ និងអាណាព្យាបាលណាខ្លះនឹងទទួល។', 'ផ្ញើ។ អាណាព្យាបាលទទួលការជូនដំណឹង ដែលបើកវិក្កយបត្រក្នុង MES Mobile។', 'អាណាព្យាបាលម្នាក់ៗទទួលការរំលឹកច្រើនបំផុតមួយដងក្នុងវិក្កយបត្រនីមួយៗ រៀងរាល់ ២៤ ម៉ោង ហើយគ្មានទេពេលវិក្កយបត្របានបង់រួច។'],
        },
      },
    ],
  },
  {
    id: 'communication',
    group: 'more',
    title: { en: 'Messages & notifications', km: 'សារ និងការជូនដំណឹង' },
    steps: [
      {
        route: 'PushNotificationsPage', page: P.push,
        text: { en: 'Send an announcement to phones.', km: 'ផ្ញើសេចក្តីប្រកាសទៅទូរស័ព្ទ។' },
        how: {
          en: ['Compose notification: recipients, category, title and message.', 'Send now, or Schedule for later with a date and time.', 'Scheduled notifications shows what was sent and the results.'],
          km: ['សរសេរការជូនដំណឹង៖ អ្នកទទួល ប្រភេទ ចំណងជើង និងសារ។', 'ផ្ញើឥឡូវ ឬ កំណត់ពេលផ្ញើក្រោយ ជាមួយកាលបរិច្ឆេទ និងម៉ោង។', 'ការជូនដំណឹងដែលបានកំណត់ពេល បង្ហាញអ្វីដែលបានផ្ញើ និងលទ្ធផល។'],
        },
      },
      {
        route: 'ChatPage', page: P.chat,
        text: { en: 'Chat with teachers, staff and parents.', km: 'ជជែកជាមួយគ្រូ បុគ្គលិក និងអាណាព្យាបាល។' },
        how: {
          en: ['New Chat: pick one or more people; a title is optional for groups.', 'Messages also reach them in MES Mobile.'],
          km: ['ការជជែកថ្មី៖ ជ្រើសមនុស្សម្នាក់ ឬច្រើន ចំណងជើងស្រេចចិត្តសម្រាប់ក្រុម។', 'សារក៏ទៅដល់គេក្នុង MES Mobile ដែរ។'],
        },
      },
      {
        route: 'ChatModerationPage', page: P.moderation,
        text: { en: 'Keep chat safe.', km: 'រក្សាការជជែកឲ្យមានសុវត្ថិភាព។' },
        how: {
          en: ['Reported Messages: review within 24 hours and Resolve.', 'Blocked Users: Unblock people when appropriate.'],
          km: ['សារដែលបានរាយការណ៍៖ ពិនិត្យក្នុងរយៈពេល ២៤ ម៉ោង ហើយ ដោះស្រាយ។', 'អ្នកប្រើដែលបានទប់ស្កាត់៖ ដោះការទប់ស្កាត់ ពេលសមរម្យ។'],
        },
      },
      {
        route: 'NotificationCategoriesPage', page: P.notificationCategories,
        text: { en: 'Group notifications into categories.', km: 'ចាត់ក្រុមការជូនដំណឹងជាប្រភេទ។' },
        how: {
          en: ['New Category: code, name, description and icon.', 'Default channels: push, email and in-app.'],
          km: ['ប្រភេទថ្មី៖ កូដ ឈ្មោះ ការពិពណ៌នា និងរូបតំណាង។', 'ឆានែលលំនាំដើម៖ push អ៊ីមែល និងក្នុងកម្មវិធី។'],
        },
      },
      {
        route: 'NotificationStatsPage', page: P.notificationStats,
        text: { en: 'See what was sent and read.', km: 'មើលអ្វីដែលបានផ្ញើ និងបានអាន។' },
        how: {
          en: ['Counts for today, this week and this month, and the read rate.', 'By category and by source, and the history with filters.', 'Automatic notifications and their logs are on Notification Triggers.'],
          km: ['ចំនួនសម្រាប់ថ្ងៃនេះ សប្តាហ៍នេះ ខែនេះ និងអត្រាអាន។', 'តាមប្រភេទ និងតាមប្រភព និងប្រវត្តិជាមួយតម្រង។', 'ការជូនដំណឹងស្វ័យប្រវត្តិ និងកំណត់ហេតុនៅ កត្តាជំរុញការជូនដំណឹង។'],
        },
      },
      {
        route: 'NotificationInboxPage', page: P.inbox,
        text: { en: 'Your own notifications.', km: 'ការជូនដំណឹងរបស់អ្នក។' },
        how: {
          en: ['Everything sent to you: filter by category, read or unread, and date.'],
          km: ['អ្វីៗដែលបានផ្ញើមកអ្នក៖ ត្រងតាមប្រភេទ បានអាន ឬមិនទាន់អាន និងកាលបរិច្ឆេទ។'],
        },
      },
    ],
  },
  {
    id: 'school-admin',
    group: 'more',
    title: { en: 'Branches, documents & system jobs', km: 'សាខា ឯកសារ និងការងារប្រព័ន្ធ' },
    steps: [
      {
        route: 'BranchPage', page: P.branches, target: action('create-branch'),
        text: { en: 'Add a branch (campus).', km: 'បន្ថែមសាខា (បរិវេណ)។' },
        how: {
          en: ['Branch code and timezone (attendance and timetables use it).', 'Contact details and location (latitude and longitude for check-in).', 'Capacity and an optional score weight base unit.'],
          km: ['កូដសាខា និងល្វែងម៉ោង (វត្តមាន និងកាលវិភាគប្រើវា)។', 'ព័ត៌មានទំនាក់ទំនង និងទីតាំង (រយៈទទឹង និងរយៈបណ្តោយសម្រាប់ចុះវត្តមាន)។', 'ចំណុះ និងឯកតាមូលដ្ឋានទម្ងន់ពិន្ទុ (ស្រេចចិត្ត)។'],
        },
      },
      {
        route: 'TemplatePage', page: P.templatesDocs, target: '[data-tour="template-create"]', check: 'documentTemplate',
        text: { en: 'Design your documents (report cards, certificates, letters).', km: 'រចនាឯកសាររបស់អ្នក (សៀវភៅតាមដាន វិញ្ញាបនបត្រ លិខិត)។' },
        how: {
          en: ['Create template opens the design workspace.', 'Save as a draft, preview, then publish when it is ready.', 'Import or export templates as JSON; Duplicate to start from one.'],
          km: ['បង្កើតគំរូ បើកកន្លែងរចនា។', 'រក្សាទុកជាសេចក្តីព្រាង មើលជាមុន រួចផ្សព្វផ្សាយពេលរួចរាល់។', 'នាំចូល ឬនាំចេញគំរូជា JSON ចម្លង ដើម្បីចាប់ផ្តើមពីគំរូមួយ។'],
        },
      },
      {
        route: 'ScheduleJobsPage', page: P.scheduleJobs,
        text: { en: 'Automatic system tasks (e.g. tomorrow\'s bus trips, reports).', km: 'កិច្ចការប្រព័ន្ធស្វ័យប្រវត្តិ (ឧ. ដំណើរឡានថ្ងៃស្អែក របាយការណ៍)។' },
        how: {
          en: ['Create Schedule Job from the predefined tasks: daily, weekly, monthly or yearly.', 'Run Now to run one at once; the history shows each run.'],
          km: ['បង្កើតការងារកំណត់ពេល ពីកិច្ចការដែលបានកំណត់ស្រាប់៖ ប្រចាំថ្ងៃ សប្តាហ៍ ខែ ឬឆ្នាំ។', 'ដំណើរការឥឡូវ ដើម្បីដំណើរការភ្លាម ប្រវត្តិបង្ហាញការដំណើរការនីមួយៗ។'],
        },
      },
    ],
  },
  {
    id: 'document-requests',
    group: 'daily',
    title: { en: 'Document requests (letters, transcripts)', km: 'សំណើឯកសារ (លិខិត ព្រឹត្តិប័ត្រពិន្ទុ)' },
    steps: [
      {
        route: 'TemplatePage', page: P.templatesDocs, target: tab('document-requests'),
        text: { en: 'Prepare a template for each document families can request.', km: 'រៀបចំគំរូសម្រាប់ឯកសារនីមួយៗដែលគ្រួសារអាចស្នើសុំ។' },
        how: {
          en: ['Open the Document request templates tab and add a template.', 'Document type: enrollment letter, transcript or certificate. Title and text in English and Khmer.', 'Style: colour, font size, logo position, page size and signatures; the preview updates as you type.', 'Only active templates can be used to issue documents.'],
          km: ['បើកផ្ទាំង គំរូឯកសារតាមសំណើ ហើយបន្ថែមគំរូ។', 'ប្រភេទឯកសារ៖ លិខិតបញ្ជាក់ការសិក្សា ព្រឹត្តិប័ត្រពិន្ទុ ឬវិញ្ញាបនបត្រ។ ចំណងជើង និងអត្ថបទជាអង់គ្លេស និងខ្មែរ។', 'រចនាប័ទ្ម៖ ពណ៌ ទំហំអក្សរ ទីតាំងនិមិត្តសញ្ញា ទំហំទំព័រ និងហត្ថលេខា ការមើលជាមុនធ្វើបច្ចុប្បន្នភាពពេលអ្នកវាយ។', 'មានតែគំរូសកម្មប៉ុណ្ណោះដែលអាចប្រើចេញឯកសារ។'],
        },
      },
      {
        route: 'SchoolDocumentsPage', page: P.documentRequests,
        text: { en: 'Review the requests from parents and students.', km: 'ពិនិត្យសំណើពីអាណាព្យាបាល និងសិស្ស។' },
        how: {
          en: ['Parents and students request documents in MES Mobile, with a purpose.', 'Filter by status or document type. Open History to see every step of a request.', 'Review: Approve, or Reject with a reason. The requester is notified.'],
          km: ['អាណាព្យាបាល និងសិស្សស្នើសុំឯកសារក្នុង MES Mobile ជាមួយគោលបំណង។', 'ត្រងតាមស្ថានភាព ឬប្រភេទឯកសារ។ បើក ប្រវត្តិ ដើម្បីមើលជំហាននីមួយៗនៃសំណើ។', 'ពិនិត្យ៖ អនុម័ត ឬបដិសេធជាមួយមូលហេតុ។ អ្នកស្នើសុំនឹងទទួលការជូនដំណឹង។'],
        },
      },
      {
        route: 'SchoolDocumentsPage', page: P.documentRequests,
        text: { en: 'Issue the approved document.', km: 'ចេញឯកសារដែលបានអនុម័ត។' },
        how: {
          en: ['Open an approved request and choose Issue document.', 'Pick the language and template, then preview the document.', 'Transcript: choose the reporting period, check the average and grade in the draft, then publish it.', 'Issue it. The requester can save or share the PDF in MES Mobile; anyone can check it with its verification link.', 'A wrong document cannot be edited: Revoke it with a reason and issue a new one.'],
          km: ['បើកសំណើដែលបានអនុម័ត ហើយជ្រើស ចេញឯកសារ។', 'ជ្រើសភាសា និងគំរូ រួចមើលឯកសារជាមុន។', 'ព្រឹត្តិប័ត្រពិន្ទុ៖ ជ្រើសវគ្គវាយតម្លៃ ពិនិត្យពិន្ទុមធ្យម និងនិទ្ទេសក្នុងសេចក្តីព្រាង រួចផ្សព្វផ្សាយ។', 'ចេញឯកសារ។ អ្នកស្នើសុំអាចរក្សាទុក ឬចែករំលែក PDF ក្នុង MES Mobile អ្នកណាក៏អាចផ្ទៀងផ្ទាត់វាតាមតំណផ្ទៀងផ្ទាត់។', 'ឯកសារខុសមិនអាចកែបានទេ៖ ដកហូតវាជាមួយមូលហេតុ ហើយចេញឯកសារថ្មី។'],
        },
      },
    ],
  },
  {
    id: 'pickup',
    group: 'daily',
    title: { en: 'Student pickup at the gate', km: 'ការទទួលសិស្សនៅមាត់ទ្វារ' },
    steps: [
      {
        route: 'SchoolPickupPage', page: P.pickup, target: action('verify'),
        text: { en: 'Hand a student only to the guardian named on the pass.', km: 'ប្រគល់សិស្សតែឲ្យអាណាព្យាបាលដែលមានឈ្មោះលើប័ណ្ណប៉ុណ្ណោះ។' },
        how: {
          en: ['The parent creates a pickup pass in MES Mobile; it shows a QR code and a countdown until it expires.', 'Scan the QR code, or type the code, and press Preview.', 'Check the student, the guardian and their photos, and confirm the guardian\'s identity in person.', 'Write the identity check note and press Confirm handover.'],
          km: ['អាណាព្យាបាលបង្កើតប័ណ្ណទទួលសិស្សក្នុង MES Mobile វាបង្ហាញកូដ QR និងពេលវេលារាប់ថយក្រោយរហូតដល់ផុតកំណត់។', 'ស្កេនកូដ QR ឬវាយកូដ ហើយចុច មើលជាមុន។', 'ពិនិត្យសិស្ស អាណាព្យាបាល និងរូបថតរបស់ពួកគេ ហើយផ្ទៀងផ្ទាត់អត្តសញ្ញាណអាណាព្យាបាលដោយផ្ទាល់។', 'សរសេរកំណត់ចំណាំការពិនិត្យអត្តសញ្ញាណ ហើយចុច បញ្ជាក់ការប្រគល់។'],
        },
      },
      {
        route: 'SchoolPickupPage', page: P.pickup,
        text: { en: 'Check the pickup history.', km: 'ពិនិត្យប្រវត្តិការទទួលសិស្ស។' },
        how: {
          en: ['Pickup history lists every pass: handed over, revoked or expired.', 'A student can be handed over once per day; used, revoked or expired passes are refused.', 'Parents get a notification when their child is handed over.'],
          km: ['ប្រវត្តិការទទួលសិស្ស បង្ហាញប័ណ្ណទាំងអស់៖ បានប្រគល់ ដកហូត ឬផុតកំណត់។', 'សិស្សម្នាក់អាចប្រគល់បានម្តងក្នុងមួយថ្ងៃ ប័ណ្ណដែលបានប្រើ ដកហូត ឬផុតកំណត់ ត្រូវបានបដិសេធ។', 'អាណាព្យាបាលទទួលការជូនដំណឹង ពេលកូនរបស់គេត្រូវបានប្រគល់។'],
        },
      },
    ],
  },
  {
    id: 'library-assets',
    group: 'more',
    title: { en: 'Library & equipment loans', km: 'បណ្ណាល័យ និងការខ្ចីសម្ភារៈ' },
    steps: [
      {
        route: 'SchoolLibraryPage', page: P.library, target: action('add-book'),
        text: { en: 'Add your books and their copies.', km: 'បន្ថែមសៀវភៅ និងច្បាប់ចម្លងរបស់វា។' },
        how: {
          en: ['Add book: title, author and ISBN.', 'Open Copies on the book and Add copy with each copy\'s barcode.'],
          km: ['បន្ថែមសៀវភៅ៖ ចំណងជើង អ្នកនិពន្ធ និង ISBN។', 'បើក ច្បាប់ចម្លង លើសៀវភៅ ហើយ បន្ថែមច្បាប់ចម្លង ជាមួយបាកូដរបស់ច្បាប់នីមួយៗ។'],
        },
      },
      {
        route: 'SchoolLibraryPage', page: P.library, target: action('loans'),
        text: { en: 'Lend books and take them back.', km: 'ឲ្យខ្ចីសៀវភៅ និងទទួលវាមកវិញ។' },
        how: {
          en: ['In Copies, Check out an available copy: search the borrower and set the due date.', 'Loans lists everything on loan; turn on Overdue only to see late books.', 'Press Return when the book comes back.'],
          km: ['ក្នុង ច្បាប់ចម្លង ឲ្យខ្ចីច្បាប់ចម្លងដែលទំនេរ៖ ស្វែងរកអ្នកខ្ចី ហើយកំណត់ថ្ងៃត្រូវសង។', 'ការខ្ចី បង្ហាញអ្វីៗដែលកំពុងខ្ចី បើក តែហួសកំណត់ ដើម្បីមើលសៀវភៅយឺត។', 'ចុច សង ពេលសៀវភៅត្រឡប់មកវិញ។'],
        },
      },
      {
        route: 'SchoolAssetsPage', page: P.assets, target: action('add-asset'),
        text: { en: 'Track equipment and who has it.', km: 'តាមដានសម្ភារៈ និងអ្នកដែលកំពុងប្រើវា។' },
        how: {
          en: ['Add asset: asset tag, name, category, serial number, location and condition.', 'Check out to a borrower with a due date; Return records the condition it came back in.', 'History shows every loan of the asset. Borrowers see their loans in MES Mobile.'],
          km: ['បន្ថែមសម្ភារៈ៖ ស្លាកសម្ភារៈ ឈ្មោះ ប្រភេទ លេខស៊េរី ទីតាំង និងស្ថានភាព។', 'ឲ្យខ្ចីទៅអ្នកខ្ចីជាមួយថ្ងៃត្រូវសង សង កត់ត្រាស្ថានភាពពេលត្រឡប់មកវិញ។', 'ប្រវត្តិ បង្ហាញការខ្ចីនីមួយៗនៃសម្ភារៈ។ អ្នកខ្ចីឃើញការខ្ចីរបស់គេក្នុង MES Mobile។'],
        },
      },
    ],
  },
  {
    id: 'teacher-storage',
    group: 'more',
    title: { en: 'Teacher file storage', km: 'ទំហំផ្ទុកឯកសាររបស់គ្រូ' },
    steps: [
      {
        route: 'TeacherStoragePage', page: P.teacherStorage, target: action('default'),
        text: { en: 'Set how much each teacher may upload.', km: 'កំណត់ថាគ្រូម្នាក់ៗអាចផ្ទុកឡើងបានប៉ុន្មាន។' },
        how: {
          en: ['Default teacher allowance: School limit only, or set an allowance in MiB.', 'For one teacher, Edit allowance: use the school default, school limit only, or their own allowance.', 'Your plan\'s school storage is always the overall limit.'],
          km: ['ទំហំលំនាំដើមរបស់គ្រូ៖ តែកម្រិតសាលា ឬកំណត់ទំហំជា MiB។', 'សម្រាប់គ្រូម្នាក់ កែទំហំ៖ ប្រើលំនាំដើមសាលា តែកម្រិតសាលា ឬទំហំផ្ទាល់ខ្លួន។', 'ទំហំផ្ទុកសាលាតាមគម្រោងរបស់អ្នក ជាកម្រិតសរុបជានិច្ច។'],
        },
      },
      {
        route: 'TeacherStoragePage', page: P.teacherStorage,
        text: { en: 'Free up space when a teacher is full.', km: 'បង្កើនទំហំទំនេរ ពេលគ្រូពេញ។' },
        how: {
          en: ['Teachers see their usage in MES Mobile and get a warning at 80% and 100%. A file that does not fit is refused before upload.', 'Open a teacher\'s files. Delete unused files; a file still used by school content cannot be deleted.', 'Verify / retry fixes uploads or deletions left pending.', 'School files: give unassigned files an owner. History lists every change.'],
          km: ['គ្រូឃើញការប្រើប្រាស់ក្នុង MES Mobile ហើយទទួលការព្រមាននៅ ៨០% និង ១០០%។ ឯកសារដែលមិនសមនឹងទំហំ ត្រូវបានបដិសេធមុនផ្ទុកឡើង។', 'បើកឯកសាររបស់គ្រូ។ លុបឯកសារមិនប្រើ ឯកសារដែលនៅប្រើក្នុងខ្លឹមសារសាលា មិនអាចលុបបានទេ។', 'ផ្ទៀងផ្ទាត់ / ព្យាយាមម្តងទៀត ដោះស្រាយការផ្ទុកឡើង ឬការលុបដែលនៅរង់ចាំ។', 'ឯកសារសាលា៖ កំណត់ម្ចាស់ឲ្យឯកសារដែលគ្មានម្ចាស់។ ប្រវត្តិ បង្ហាញការផ្លាស់ប្តូរទាំងអស់។'],
        },
      },
    ],
  },
  {
    id: 'university',
    group: 'more',
    title: { en: 'University: faculties & programs', km: 'សាកលវិទ្យាល័យ៖ មហាវិទ្យាល័យ និងកម្មវិធី' },
    steps: [
      {
        route: 'FacultyPage', page: P.faculties, target: action('create'), check: 'faculty',
        text: { en: 'Add faculties.', km: 'បន្ថែមមហាវិទ្យាល័យ។' },
        how: {
          en: ['Add Faculty: name in English and Khmer (e.g. Faculty of Engineering) and a description.'],
          km: ['បន្ថែមមហាវិទ្យាល័យ៖ ឈ្មោះជាអង់គ្លេស និងខ្មែរ (ឧ. មហាវិទ្យាល័យវិស្វកម្ម) និងការពិពណ៌នា។'],
        },
      },
      {
        route: 'DepartmentPage', page: P.departments, target: action('create'), check: 'department',
        text: { en: 'Add departments inside each faculty.', km: 'បន្ថែមនាយកដ្ឋានក្នុងមហាវិទ្យាល័យនីមួយៗ។' },
        how: {
          en: ['Add Department: faculty, name in English and Khmer (e.g. Computer Science) and a description.'],
          km: ['បន្ថែមនាយកដ្ឋាន៖ មហាវិទ្យាល័យ ឈ្មោះជាអង់គ្លេស និងខ្មែរ (ឧ. វិទ្យាសាស្ត្រកុំព្យូទ័រ) និងការពិពណ៌នា។'],
        },
      },
      {
        route: 'ProgramPage', page: P.programs, target: action('create'), check: 'program',
        text: { en: 'Add degree programs.', km: 'បន្ថែមកម្មវិធីសញ្ញាបត្រ។' },
        how: {
          en: ['Add Program: degree type, department, name (e.g. Bachelor of Computer Science), total credits and duration in years.', 'Students are then enrolled in programs from the Students page.'],
          km: ['បន្ថែមកម្មវិធី៖ ប្រភេទសញ្ញាបត្រ នាយកដ្ឋាន ឈ្មោះ (ឧ. បរិញ្ញាបត្រវិទ្យាសាស្ត្រកុំព្យូទ័រ) ក្រេឌីតសរុប និងរយៈពេលជាឆ្នាំ។', 'បន្ទាប់មក សិស្សត្រូវចុះឈ្មោះក្នុងកម្មវិធីពីទំព័រសិស្ស។'],
        },
      },
    ],
  },
  {
    id: 'training',
    group: 'more',
    title: { en: 'Short courses & certificates', km: 'វគ្គខ្លី និងវិញ្ញាបនបត្រ' },
    steps: [
      {
        route: 'ShortCoursesPage', page: P.shortCourses, target: action('create'), check: 'shortCourse',
        text: { en: 'Create training courses.', km: 'បង្កើតវគ្គបណ្តុះបណ្តាល។' },
        how: {
          en: ['Add Course: level, name in English and Khmer, format, total hours, price and description.', 'Publish the course when it is ready.'],
          km: ['បន្ថែមវគ្គ៖ កម្រិត ឈ្មោះជាអង់គ្លេស និងខ្មែរ ទម្រង់ ម៉ោងសរុប តម្លៃ និងការពិពណ៌នា។', 'ផ្សព្វផ្សាយវគ្គ ពេលរួចរាល់។'],
        },
      },
      {
        route: 'CourseBatchesPage', page: P.batches, target: action('create'), check: 'batch',
        text: { en: 'Open a batch (cohort) of a course.', km: 'បើកជំនាន់ (ក្រុម) នៃវគ្គមួយ។' },
        how: {
          en: ['Add Batch: course, batch number, start and end dates, location, room, maximum participants, price and schedule.', 'Registration Open lets people join. Start the batch, then Complete it at the end.'],
          km: ['បន្ថែមជំនាន់៖ វគ្គ លេខជំនាន់ ថ្ងៃចាប់ផ្តើម និងបញ្ចប់ ទីតាំង បន្ទប់ អ្នកចូលរួមអតិបរមា តម្លៃ និងកាលវិភាគ។', 'បើកការចុះឈ្មោះ ឲ្យមនុស្សចូលរួម។ ចាប់ផ្តើមជំនាន់ រួច បញ្ចប់ វានៅចុងក្រោយ។'],
        },
      },
      {
        route: 'CertificatesPage', page: P.certificates, target: action('create-template'), check: 'certificateTemplate',
        text: { en: 'Design the certificates people receive.', km: 'រចនាវិញ្ញាបនបត្រដែលអ្នកចូលរួមទទួលបាន។' },
        how: {
          en: ['Add Template: name, description, primary and secondary colors; Set as default.', 'Issued certificates can be revoked here (this cannot be undone).'],
          km: ['បន្ថែមគំរូ៖ ឈ្មោះ ការពិពណ៌នា ពណ៌ចម្បង និងពណ៌បន្ទាប់ កំណត់ជាលំនាំដើម។', 'វិញ្ញាបនបត្រដែលបានចេញ អាចដកហូតនៅទីនេះ (មិនអាចត្រឡប់វិញបានទេ)។'],
        },
      },
    ],
  },
  {
    id: 'daily-overview',
    group: 'daily',
    title: { en: 'Daily school overview', km: 'ទិដ្ឋភាពសាលាប្រចាំថ្ងៃ' },
    steps: [
      {
        route: 'DashboardPage', page: P.dashboard,
        text: { en: 'Start your day on the dashboard.', km: 'ចាប់ផ្តើមថ្ងៃរបស់អ្នកនៅផ្ទាំងគ្រប់គ្រង។' },
        how: {
          en: ['Today: present, late and absent students, and teachers who checked in late.', 'Exams this week, totals, and change compared with last year.', 'Quick Actions open common tasks.'],
          km: ['ថ្ងៃនេះ៖ សិស្សមានវត្តមាន យឺត និងអវត្តមាន និងគ្រូដែលចុះវត្តមានយឺត។', 'ការប្រឡងសប្តាហ៍នេះ សរុប និងការផ្លាស់ប្តូរធៀបនឹងឆ្នាំមុន។', 'សកម្មភាពរហ័ស បើកកិច្ចការទូទៅ។'],
        },
      },
      {
        route: 'AdminDashboardPage', page: P.adminDashboard,
        text: { en: 'Several branches? See them all together.', km: 'មានសាខាច្រើន? មើលទាំងអស់ជាមួយគ្នា។' },
        how: {
          en: ['Students, teachers and classes across all branches, and each branch\'s seats used.', 'Quick links to Manage Branches, School Settings and reports.'],
          km: ['សិស្ស គ្រូ និងថ្នាក់ នៅគ្រប់សាខា និងកៅអីដែលប្រើក្នុងសាខានីមួយៗ។', 'តំណរហ័សទៅ គ្រប់គ្រងសាខា ការកំណត់សាលា និងរបាយការណ៍។'],
        },
      },
      {
        route: 'AIDashboardPage', page: P.aiDashboard,
        text: { en: 'Check your AI assistant usage.', km: 'ពិនិត្យការប្រើប្រាស់ជំនួយការ AI។' },
        how: {
          en: ['AI usage this month, your plan\'s limit and days left in the billing period.'],
          km: ['ការប្រើ AI ខែនេះ ដែនកំណត់កញ្ចប់សេវា និងថ្ងៃនៅសល់ក្នុងវគ្គគិតប្រាក់។'],
        },
      },
    ],
  },
  {
    id: 'teacher',
    group: 'people',
    title: { en: 'Register a teacher', km: 'ចុះឈ្មោះគ្រូ' },
    steps: [
      {
        route: 'TeacherPage', page: P.teacher, target: action('create-teacher'), waitFor: form('teacher', 1),
        text: { en: 'Each teacher gets a login for MES Mobile: check-in, attendance, scores and leave.', km: 'គ្រូម្នាក់ៗទទួលបានគណនីសម្រាប់ MES Mobile៖ ចុះវត្តមាន វត្តមានសិស្ស ពិន្ទុ និងច្បាប់។' },
        how: {
          en: ['Press Create Teacher. The form has 4 steps; I explain each one.', 'Before you start: the subjects, courses and classes this teacher teaches must already exist.'],
          km: ['ចុច បង្កើតគ្រូ។ ទម្រង់មាន ៤ ជំហាន ខ្ញុំពន្យល់ម្តងមួយៗ។', 'មុនចាប់ផ្តើម៖ មុខវិជ្ជា វគ្គសិក្សា និងថ្នាក់ដែលគ្រូនេះបង្រៀន ត្រូវតែមានរួចហើយ។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher, waitFor: form('teacher', 2),
        text: { en: 'Step 1 · Information: who the teacher is.', km: 'ជំហាន ១ · ព័ត៌មាន៖ គ្រូនេះជានរណា។' },
        how: {
          en: ['First and last name: shown to students and parents.', 'Phone number: their contact. It can also be their username (step 3).', 'Email, gender, date of birth and address: optional, for your records.', 'Press Next.'],
          km: ['នាមខ្លួន និងនាមត្រកូល៖ បង្ហាញដល់សិស្ស និងអាណាព្យាបាល។', 'លេខទូរស័ព្ទ៖ សម្រាប់ទំនាក់ទំនង។ អាចប្រើជាឈ្មោះអ្នកប្រើ (ជំហាន ៣)។', 'អ៊ីមែល ភេទ ថ្ងៃខែឆ្នាំកំណើត និងអាសយដ្ឋាន៖ ស្រេចចិត្ត សម្រាប់ឯកសាររបស់អ្នក។', 'ចុច បន្ទាប់។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher, waitFor: form('teacher', 3),
        text: { en: 'Step 2 · Teaching: what and where they teach. This links the teacher to the class timetable and score entry.', km: 'ជំហាន ២ · ការបង្រៀន៖ បង្រៀនអ្វី និងនៅណា។ វាភ្ជាប់គ្រូទៅកាលវិភាគថ្នាក់ និងការបញ្ចូលពិន្ទុ។' },
        how: {
          en: ['Subject and years of experience: their speciality. The proficiency level is worked out for you.', 'Branch and academic year: where and when they teach.', 'Grade, class and course: the class and course they teach. Course lists only courses of that grade.', 'Teacher role and weekly hours: their role in that course and how many hours a week.', 'Assign as Homeroom Teacher: they become the class teacher. They approve short leave and confirm the class monitor\'s attendance.', 'Enable Auto Assignment: instead of picking a class, the system finds classes that need their course, based on workload. Press Next.'],
          km: ['មុខវិជ្ជា និងឆ្នាំបទពិសោធន៍៖ ជំនាញរបស់គេ។ កម្រិតជំនាញនឹងគណនាដោយស្វ័យប្រវត្តិ។', 'សាខា និងឆ្នាំសិក្សា៖ បង្រៀននៅណា និងពេលណា។', 'កម្រិតថ្នាក់ ថ្នាក់ និងវគ្គសិក្សា៖ ថ្នាក់ និងវគ្គសិក្សាដែលគេបង្រៀន។ វគ្គសិក្សាបង្ហាញតែវគ្គនៃកម្រិតនោះ។', 'តួនាទីគ្រូ និងម៉ោងប្រចាំសប្តាហ៍៖ តួនាទីក្នុងវគ្គនោះ និងចំនួនម៉ោងក្នុងមួយសប្តាហ៍។', 'កំណត់ជាគ្រូប្រចាំថ្នាក់៖ គេក្លាយជាគ្រូប្រចាំថ្នាក់។ គេអនុម័តច្បាប់ខ្លី និងបញ្ជាក់វត្តមានដែលប្រធានថ្នាក់កត់។', 'ការចាត់តាំងស្វ័យប្រវត្តិ៖ ជំនួសការជ្រើសថ្នាក់ ប្រព័ន្ធរកថ្នាក់ដែលត្រូវការវគ្គសិក្សារបស់គេ តាមបន្ទុកការងារ។ ចុច បន្ទាប់។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher, waitFor: form('teacher', 4),
        text: { en: 'Step 3 · Login for MES Mobile.', km: 'ជំហាន ៣ · គណនីសម្រាប់ MES Mobile។' },
        how: {
          en: ['Username: tick Auto use phone number, or type one.', 'Password: tick Auto generate password, or type one.', 'Press Next.'],
          km: ['ឈ្មោះអ្នកប្រើ៖ ធីក ប្រើលេខទូរស័ព្ទស្វ័យប្រវត្តិ ឬវាយបញ្ចូល។', 'ពាក្យសម្ងាត់៖ ធីក បង្កើតពាក្យសម្ងាត់ស្វ័យប្រវត្តិ ឬវាយបញ្ចូល។', 'ចុច បន្ទាប់។'],
        },
      },
      {
        route: 'TeacherPage', page: P.teacher, countUp: 'teachers',
        text: { en: 'Step 4 · Review and create.', km: 'ជំហាន ៤ · ពិនិត្យ និងបង្កើត។' },
        how: {
          en: ['Check the details, then press Create.', 'A box shows the username and password. Copy them and give them to the teacher for MES Mobile.', 'Repeat for each teacher.'],
          km: ['ពិនិត្យព័ត៌មាន រួចចុច បង្កើត។', 'ប្រអប់មួយបង្ហាញឈ្មោះអ្នកប្រើ និងពាក្យសម្ងាត់។ ចម្លង ហើយផ្តល់ឲ្យគ្រូប្រើ MES Mobile។', 'ធ្វើម្តងទៀតសម្រាប់គ្រូនីមួយៗ។'],
        },
      },
    ],
  },
  {
    id: 'student',
    title: { en: 'Register a student', km: 'ចុះឈ្មោះសិស្ស' },
    hidden: true,
    steps: STUDENT_FORM_STEPS,
  },
];

const STORE_KEY = 'mes-guide-walkthrough';

type State = { flow: string; step: number; ret?: { flow: string; step: number } };

const load = (): State | null => {
  try {
    const v = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null');
    return WALK_FLOWS.some((f) => f.id === v?.flow) ? v : null;
  } catch {
    return null;
  }
};

const state = ref(load());

const save = () => {
  try {
    if (state.value) localStorage.setItem(STORE_KEY, JSON.stringify(state.value));
    else localStorage.removeItem(STORE_KEY);
  } catch { /* storage blocked: progress lives in memory only */ }
};

export const activeFlow = computed(() => WALK_FLOWS.find((f) => f.id === state.value?.flow) ?? null);
export const stepIndex = computed(() => state.value?.step ?? 0);
export const activeStep = computed(() => activeFlow.value?.steps[stepIndex.value] ?? null);

// A sub-guide started from a step ("Show me step by step") returns to the next step when done.
export const parentFlow = computed(() => WALK_FLOWS.find((f) => f.id === state.value?.ret?.flow) ?? null);

export const startWalkthrough = (flow: string, nested = false) => {
  const ret = nested && state.value ? { flow: state.value.flow, step: state.value.step } : undefined;
  state.value = { flow, step: 0, ret };
  save();
};

export const goToStep = (step: number) => {
  if (!state.value) return;
  const last = activeFlow.value!.steps.length - 1;
  const ret = state.value.ret;
  if (step <= last) state.value = { ...state.value, step: Math.max(0, step) };
  else state.value = ret ? { flow: ret.flow, step: ret.step + 1 } : null;
  if (state.value && state.value.step >= (activeFlow.value?.steps.length ?? 0)) state.value = null;
  save();
};

export const stopWalkthrough = () => {
  state.value = null;
  save();
};
